import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography, Tabs, Tab, Checkbox, Button, FormControlLabel, ImageList, ImageListItem, useMediaQuery } from '@mui/material';
import { Height, Refresh, ViewModuleOutlined } from '@mui/icons-material';
import { useTheme as useAppTheme } from '../../../context/ThemeContext';
import ZoneProcessor, { ZoneNames } from "./utils/zoneProcessor";
import FrameProcessorPressure from './utils/FrameProcessorPressure';
import { useDesignStore } from 'context/DesignContext';
import PressureViewerComponent, { PressureViewerHandler } from "Scan/PressureViewer";
import PressureRenderOptions from "Scan/PressureRenderOptions";
import { InsoleType } from 'models/Insole';
import Line from './utils/Line';
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import PrintViewer from "./PdfReport";
import cv from "@techstark/opencv-js";
import "./PressureExamViewer.css";

export const fp = new FrameProcessorPressure();
type DraftDimensions = { axis: Line; width: Line; heel: Line };
type DraftZones = { controlPoints: cv.Point[] };

export const Viewer: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const fullCanvasRef = useRef<HTMLCanvasElement>(null);
  const fullPdfCanvasRef = useRef<HTMLCanvasElement>(null);

  const [activeTab, setActiveTab] = useState(0);
  const [previousTab, setPreviousTab] = useState(0);
  const [showDimensions, setShowDimensions] = useState(false);
  const [showZones, setShowZones] = useState(false);
  const [showMeasurements, setShowMeasurements] = useState(false);
  const [showCursors, setShowCursors] = useState(false);

  const [multiStep, setMultiStep] = useState(false);
  const [leftGallery, setLeftGallery] = useState<string[]>([]);
  const [rightGallery, setRightGallery] = useState<string[]>([]);

  const [leftZones, setLeftZones] = useState<ZoneProcessor | undefined>();
  const [rightZones, setRightZones] = useState<ZoneProcessor | undefined>();

  const { isDarkMode } = useAppTheme();

  /**
   * Phones and tablets (anything the app does not treat as desktop) get a
   * single-column layout: the panel grows to fit its content and the modal body
   * owns the only scrollbar, instead of this box being a fixed 700px tall
   * horizontal scroller. Desktop (>= 1024px) is untouched.
   */
  const isNarrowLayout = useMediaQuery('(max-width: 1023px)');
  /**
   * A phone in landscape is short but wide, so stacking everything in one
   * column leaves each panel stretched across the screen and adds a lot of
   * vertical scrolling. Below 600px (every phone in portrait) we stack; from
   * 600px up we keep things side by side and spend the width instead.
   */
  const isStackedLayout = useMediaQuery('(max-width: 599px)');
  /** Phone/small tablet in landscape: mobile treatment, but width to spare. */
  const isLandscapeLayout = isNarrowLayout && !isStackedLayout;
  const cellPad = isNarrowLayout ? 0.4 : 0.75;
  const cellFontSize = isNarrowLayout ? '0.72rem' : '0.8rem';

  const pressureData = useDesignStore(state => state.insoleDesign?.pressureData);

  const leftPressureViewer = useRef<PressureViewerHandler>(null);
  const rightPressureViewer = useRef<PressureViewerHandler>(null);
  const galleryHandler = useRef<PressureViewerHandler>(null);

  const isDynamic = pressureData?.ExamType === 'dynamic';

  const renderOptions = useRef<PressureRenderOptions>(new PressureRenderOptions());

  const printRef = useRef<HTMLDivElement>(null);
  const [fullCanvasUrl, setFullCanvasUrl] = useState<string | undefined>(undefined);
  const onFullCanvasReady = useCallback((url: string) => setFullCanvasUrl(url), []);

  const [leftDraft, setLeftDraft] = useState<{ dimensions?: DraftDimensions; zones?: DraftZones } | null>(null);
  const [rightDraft, setRightDraft] = useState<{ dimensions?: DraftDimensions; zones?: DraftZones } | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  const [measurements, setMeasurements] = useState({
    left: {
      pressure: 0,
      foreFoot: 0,
      midFoot: 0,
      rearFoot: 0,
      surface: 0,
      totalAvg: 0,
      foreFootAvg: 0,
      rearFootAvg: 0,
      foreFootImpulse: 0,
      midFootImpulse: 0,
      rearFootImpulse: 0,
      footType: '',
      axis: 0,
      index: 0,
      footLength: 0,
      footWidth: 0,
      heelWidth: 0,
    },
    right: {
      pressure: 0,
      foreFoot: 0,
      midFoot: 0,
      rearFoot: 0,
      surface: 0,
      totalAvg: 0,
      foreFootAvg: 0,
      rearFootAvg: 0,
      foreFootImpulse: 0,
      midFootImpulse: 0,
      rearFootImpulse: 0,
      footType: '',
      axis: 0,
      index: 0,
      footLength: 0,
      footWidth: 0,
      heelWidth: 0,
    },
  });

  const measurementsRef = useRef(measurements);
  const firstTimeZoneCalculation = useRef(true);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // useEffect(() => {
  //   measurementsRef.current = measurements;
  // }, [measurements]);

  const [activeEdit, setActiveEdit] = useState<'dimensions' | 'zones' | 'none' | null>(null);
  const [prevShowDimensions, setPrevShowDimensions] = useState(false);
  const [prevShowZones, setPrevShowZones] = useState(false);
  const isEditing = activeEdit === 'dimensions' || activeEdit === 'zones';

  useEffect(() => {
    try {
      if (!pressureData || !pressureData.CalibFrames_Left?.length) {
        setError("No pressure data found.");
        return;
      }

      renderOptions.current.Draw_ZonesOverlay = showZones;
      renderOptions.current.Draw_WidthLine = showDimensions;
      renderOptions.current.Draw_AxisLine = showDimensions;
      renderOptions.current.Draw_HeelLine = showDimensions;

      if (!activeEdit || activeEdit === 'none') {
        leftPressureViewer.current?.setRenderOptions(renderOptions.current);
        rightPressureViewer.current?.setRenderOptions(renderOptions.current);
      }

    } catch (e: any) {
      setError(e.message);
    }
  }, [showDimensions, showZones]);

  useEffect(() => {
    if (activeTab === 0 && !isDynamic) {
      if (pressureData == null) return;

      CreateCOGBitmap(pressureData.ROI_Left, pressureData.ROI_Right, pressureData.COPs_Left[0], pressureData.COPs_Right[0]);
    }
  }, [activeTab]);

  useEffect(() => {
    if (!pressureData || !leftPressureViewer.current || !rightPressureViewer.current) return;

    const isDynamic = pressureData.ExamType === 'dynamic';

    if (isDynamic && isEditing) {
      // EDIT MODE (dynamic): show only max frame and pause
      // leftPressureViewer.current.setCalibFrameArray([pressureData.CalibMaxFrame_Left]);
      // rightPressureViewer.current.setCalibFrameArray([pressureData.CalibMaxFrame_Right]);

      leftPressureViewer.current.Pause();
      rightPressureViewer.current.Pause();
    } else if (isDynamic && !isEditing) {
      // NORMAL: restore full array (do NOT pause here)
      // leftPressureViewer.current.setCalibFrameArray(pressureData.CalibFrames_Left);
      // rightPressureViewer.current.setCalibFrameArray(pressureData.CalibFrames_Right);

      leftPressureViewer.current.updateChartSeries();
      rightPressureViewer.current.updateChartSeries();

      leftPressureViewer.current?.Play();
      rightPressureViewer.current?.Play();
    }

  }, [activeEdit]);

  const UpdateView = (reCalculateZones: boolean, saveChanges: boolean = true) => {
    if (!pressureData || !leftPressureViewer.current || !rightPressureViewer.current) return;

    if (pressureData.status == "scanned") {
      DoAutomaticCalculations(true, true, reCalculateZones);
      pressureData.status = "Done";

      if (saveChanges) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
          pressureData?.Save();
        }, 5000);
      }
    }

    SetCompleteLayout();
    renderOptions.current.Draw_COP = true;
    renderOptions.current.Draw_COP_Line = true;
    renderOptions.current.Draw_ProcessROI = true;

    renderOptions.current.Draw_AxisLine = showDimensions;
    renderOptions.current.Draw_WidthLine = showDimensions;
    renderOptions.current.Draw_HeelLine = showDimensions;
    renderOptions.current.Draw_ZonesOverlay = showZones;
    renderOptions.current.Draw_Measurements = showMeasurements;
    renderOptions.current.Draw_Cursors = showCursors;

    if (pressureData.ExamType == 'static') {
      if (pressureData.CalibFrames_Left.length > 0) {
        CalculateStaticTestFootType();
        CalculateStaticParameters(pressureData.CalibFrames_Left[0], pressureData.ROI_Right, pressureData.ROI_Left);
      }
    }
    else if (pressureData.ExamType == 'dynamic') {
      FillDynamicAnalyseData();
    }

    renderOptions.current.Draw_Zone_FillPolygons = false;
    renderOptions.current.Draw_Zone_Sensors = false;

    leftPressureViewer.current.setRenderOptions(renderOptions.current);
    rightPressureViewer.current.setRenderOptions(renderOptions.current);

    // Add measurements and cursor data to table for measurements and cursors
    // gridControlMeasures.DataSource = mPressureData.PressureMesurements;
    // if (gridViewMeasures.Columns.Count > 2) {
    //   gridViewMeasures.Columns[1].DisplayFormat.FormatType = DevExpress.Utils.FormatType.Custom;
    //   gridViewMeasures.Columns[1].DisplayFormat.FormatString = "0.0";
    // }
    // gridControlCursor.DataSource = mPressureData.PressureCursors;
    // if (gridViewCursors.Columns.Count > 2) {
    //   gridViewCursors.Columns[1].DisplayFormat.FormatType = DevExpress.Utils.FormatType.Custom;
    //   gridViewCursors.Columns[1].DisplayFormat.FormatString = "0.0";
    // }

    // leftPressureViewer.current.SetMeasurements(pressureData.PressureMesurements, 'Left');
    // rightPressureViewer.current.SetMeasurements(pressureData.PressureMesurements, 'Right');

    // leftPressureViewer.current.SetCursors(pressureData.PressureCursors, 'Left');
    // rightPressureViewer.current.SetCursors(pressureData.PressureCursors, 'Right');
    rightPressureViewer.current.updateChartSeries();
    leftPressureViewer.current.updateChartSeries();

    leftPressureViewer.current.Play();
    rightPressureViewer.current.Play();
  }

  const SetCompleteLayout = () => {
    if (!pressureData || !leftPressureViewer.current || !rightPressureViewer.current) return;

    leftPressureViewer.current.setPressureData(pressureData.ROI_Left, pressureData.COPs_Left, pressureData.Axis_Left, pressureData.WidthLine_Left, pressureData.HeelLine_Left);
    rightPressureViewer.current.setPressureData(pressureData.ROI_Right, pressureData.COPs_Right, pressureData.Axis_Right, pressureData.WidthLine_Right, pressureData.HeelLine_Right);
    leftPressureViewer.current.setZones(pressureData.Zones_Left);
    rightPressureViewer.current.setZones(pressureData.Zones_Right);

    if (pressureData.MetCenter.length === 0) {
      CalculateMarkers();
      pressureData?.Save();
    }

    leftPressureViewer.current.setCropView();
    rightPressureViewer.current.setCropView();
  }

  const CalculateMarkers = () => {
    if (!pressureData || !leftPressureViewer.current || !rightPressureViewer.current) return;

    let lmetcenter;
    let lheelcenter;
    let rmetcenter;
    let rheelcenter;

    // if (leftFootRegistrationFailure || mPressureData.LeftControlPoints == null) {
    if (pressureData.LeftControlPoints.length === 0) {
      var lm1center = leftPressureViewer.current.getZoneCenter(ZoneNames.M1);
      var lm5center = leftPressureViewer.current.getZoneCenter(ZoneNames.M5);
      lmetcenter = new cv.Point((lm1center.x + lm5center.x) / 2, (lm1center.y + lm5center.y) / 2);
      lheelcenter = leftPressureViewer.current.getHeelCenterOfPressure();
    }
    else {
      lheelcenter = leftPressureViewer.current.getRealSizedPoint(pressureData.LeftControlPoints[22]);
      lmetcenter = leftPressureViewer.current.getRealSizedPoint(pressureData.LeftControlPoints[21]);
    }

    // if (rightFootRegistrationFailure || mPressureData.RightControlPoints == null) {
    if (pressureData.RightControlPoints.length === 0) {
      var rm1center = rightPressureViewer.current.getZoneCenter(ZoneNames.M1);
      var rm5center = rightPressureViewer.current.getZoneCenter(ZoneNames.M5);
      rmetcenter = new cv.Point((rm1center.x + rm5center.x) / 2, (rm1center.y + rm5center.y) / 2);
      rheelcenter = rightPressureViewer.current.getHeelCenterOfPressure();
    }
    else {
      rheelcenter = rightPressureViewer.current.getRealSizedPoint(pressureData.RightControlPoints[22]);
      rmetcenter = rightPressureViewer.current.getRealSizedPoint(pressureData.RightControlPoints[21]);
    }

    pressureData.MetCenter = [rmetcenter, lmetcenter];
    pressureData.HeelCenter = [rheelcenter, lheelcenter];

    var rm1cop = rightPressureViewer.current.getZoneCenterOfPressureMinThr(ZoneNames.M1, 0.7);
    var lm1cop = leftPressureViewer.current.getZoneCenterOfPressureMinThr(ZoneNames.M1, 0.7);
    var rm1p = rightPressureViewer.current.getZonePressure(ZoneNames.M1);
    var lm1p = leftPressureViewer.current.getZonePressure(ZoneNames.M1);
    pressureData.M1COP = [rm1cop, lm1cop];
    pressureData.M1P = [rm1p, lm1p];

    var rm2cop = rightPressureViewer.current.getZoneCenterOfPressureMinThr(ZoneNames.M2, 0.7);
    var lm2cop = leftPressureViewer.current.getZoneCenterOfPressureMinThr(ZoneNames.M2, 0.7);
    var rm2p = rightPressureViewer.current.getZonePressure(ZoneNames.M2);
    var lm2p = leftPressureViewer.current.getZonePressure(ZoneNames.M2);
    pressureData.M2COP = [rm2cop, lm2cop];
    pressureData.M2P = [rm2p, lm2p];

    var rm3cop = rightPressureViewer.current.getZoneCenterOfPressureMinThr(ZoneNames.M3, 0.7);
    var lm3cop = leftPressureViewer.current.getZoneCenterOfPressureMinThr(ZoneNames.M3, 0.7);
    var rm3p = rightPressureViewer.current.getZonePressure(ZoneNames.M3);
    var lm3p = leftPressureViewer.current.getZonePressure(ZoneNames.M3);
    pressureData.M3COP = [rm3cop, lm3cop];
    pressureData.M3P = [rm3p, lm3p];

    var rm4cop = rightPressureViewer.current.getZoneCenterOfPressureMinThr(ZoneNames.M4, 0.7);
    var lm4cop = leftPressureViewer.current.getZoneCenterOfPressureMinThr(ZoneNames.M4, 0.7);
    var rm4p = rightPressureViewer.current.getZonePressure(ZoneNames.M4);
    var lm4p = leftPressureViewer.current.getZonePressure(ZoneNames.M4);
    pressureData.M4COP = [rm4cop, lm4cop];
    pressureData.M4P = [rm4p, lm4p];

    var rm5cop = rightPressureViewer.current.getZoneCenterOfPressureMinThr(ZoneNames.M5, 0.7);
    var lm5cop = leftPressureViewer.current.getZoneCenterOfPressureMinThr(ZoneNames.M5, 0.7);
    var rm5p = rightPressureViewer.current.getZonePressure(ZoneNames.M5);
    var lm5p = leftPressureViewer.current.getZonePressure(ZoneNames.M5);
    pressureData.M5COP = [rm5cop, lm5cop];
    pressureData.M5P = [rm5p, lm5p];
  }

  const CalculateStaticParameters = (Frame: cv.Mat, RightROI: cv.Rect, LeftROI: cv.Rect) => {
    const RightCOP = fp.ComputeCOP(Frame, RightROI);
    const LeftCOP = fp.ComputeCOP(Frame, LeftROI);

    const RightPressure = fp.GetSumPressure(fp.GetAllROISensors(RightROI), Frame);
    const LeftPressure = fp.GetSumPressure(fp.GetAllROISensors(LeftROI), Frame);
    const TotalPressure = RightPressure + LeftPressure;

    var Left_PressurePerc = (LeftPressure / TotalPressure);
    var Right_PressurePerc = (RightPressure / TotalPressure);

    var RightUpRect = new cv.Rect(RightROI.x, RightROI.y, RightROI.width, RightCOP.y - RightROI.y);
    var RightDownRect = new cv.Rect(RightROI.x, RightCOP.y + 1, RightROI.width, (RightROI.y + RightROI.height) - RightCOP.y);
    var LeftUpRect = new cv.Rect(LeftROI.x, LeftROI.y, LeftROI.width, LeftCOP.y - LeftROI.y);
    var LeftDownRect = new cv.Rect(LeftROI.x, LeftCOP.y + 1, LeftROI.width, (LeftROI.y + LeftROI.height) - LeftCOP.y);

    var RightUpPressure = fp.GetSumPressure(fp.GetAllROISensors(RightUpRect), Frame);
    var RightDownPressure = fp.GetSumPressure(fp.GetAllROISensors(RightDownRect), Frame);
    var LeftUpPressure = fp.GetSumPressure(fp.GetAllROISensors(LeftUpRect), Frame);
    var LeftDownPressure = fp.GetSumPressure(fp.GetAllROISensors(LeftDownRect), Frame);

    var RightFootAverage = fp.GetAveragePressure(fp.GetAllROISensors(RightROI), Frame);
    var LeftFootAverage = fp.GetAveragePressure(fp.GetAllROISensors(LeftROI), Frame);

    var RightUpAverage = fp.GetAveragePressure(fp.GetAllROISensors(RightUpRect), Frame);
    var RightDownAverage = fp.GetAveragePressure(fp.GetAllROISensors(RightDownRect), Frame);
    var LeftUpAverage = fp.GetAveragePressure(fp.GetAllROISensors(LeftUpRect), Frame);
    var LeftDownAverage = fp.GetAveragePressure(fp.GetAllROISensors(LeftDownRect), Frame);

    var RightFootSurface = fp.GetSurface(fp.GetAllROISensors(RightROI), Frame);
    var LeftFootSurface = fp.GetSurface(fp.GetAllROISensors(LeftROI), Frame);

    var Total4 = LeftUpPressure + RightUpPressure + LeftDownPressure + RightDownPressure;

    var Left_ForeFootPerc = LeftUpPressure / Total4;
    var Right_ForeFootPerc = RightUpPressure / Total4;

    var Left_RearFootPerc = LeftDownPressure / Total4;
    var Right_RearFootPerc = RightDownPressure / Total4;

    const leftFootLength = Math.round(fp.getRealDistance(pressureData?.Axis_Left.start, pressureData?.Axis_Left.end)) / 10;
    const rightFootLength = Math.round(fp.getRealDistance(pressureData?.Axis_Right.start, pressureData?.Axis_Right.end)) / 10;

    const leftFootWidth = Math.round(fp.getRealDistance(pressureData?.WidthLine_Left.start, pressureData?.WidthLine_Left.end)) / 10;
    const rightFootWidth = Math.round(fp.getRealDistance(pressureData?.WidthLine_Right.start, pressureData?.WidthLine_Right.end)) / 10;

    const leftFootHeel = Math.round(fp.getRealDistance(pressureData?.HeelLine_Left.start, pressureData?.HeelLine_Left.end)) / 10;
    const rightFootHeel = Math.round(fp.getRealDistance(pressureData?.HeelLine_Right.start, pressureData?.HeelLine_Right.end)) / 10;

    setMeasurements(prev => {
      const updated = {
        ...prev,
        left: {
          ...prev.left,
          pressure: Math.round(Left_PressurePerc * 10000) / 100,
          foreFoot: Math.round(Left_ForeFootPerc * 10000) / 100,
          rearFoot: Math.round(Left_RearFootPerc * 10000) / 100,
          surface: Math.round(LeftFootSurface * 100) / 100,
          totalAvg: Math.round(LeftFootAverage * 100) / 100,
          foreFootAvg: Math.round(LeftUpAverage * 100) / 100,
          rearFootAvg: Math.round(LeftDownAverage * 100) / 100,
          footLength: Math.round(leftFootLength * 100) / 100,
          footWidth: Math.round(leftFootWidth * 100) / 100,
          heelWidth: Math.round(leftFootHeel * 100) / 100,
          footType: GetFootType(pressureData?.FootIndex_Left).toString(),
        },
        right: {
          ...prev.right,
          pressure: Math.round(Right_PressurePerc * 10000) / 100,
          foreFoot: Math.round(Right_ForeFootPerc * 10000) / 100,
          rearFoot: Math.round(Right_RearFootPerc * 10000) / 100,
          surface: Math.round(RightFootSurface * 100) / 100,
          totalAvg: Math.round(RightFootAverage * 100) / 100,
          foreFootAvg: Math.round(RightUpAverage * 100) / 100,
          rearFootAvg: Math.round(RightDownAverage * 100) / 100,
          footLength: Math.round(rightFootLength * 100) / 100,
          footWidth: Math.round(rightFootWidth * 100) / 100,
          heelWidth: Math.round(rightFootHeel * 100) / 100,
          footType: GetFootType(pressureData?.FootIndex_Right).toString(),
        }
      };
      measurementsRef.current = updated;
      return updated;
    });

    CreateCOGBitmap(LeftROI, RightROI, LeftCOP, RightCOP);
  }

  const CreateCOGBitmap = async (LeftROI: cv.Rect, RightROI: cv.Rect, LeftCOP: cv.Point, RightCOP: cv.Point) => {
    //Calculating COP X distances to center;
    var dxl = (LeftROI.x + LeftROI.width) - LeftCOP.x;
    var dxr = RightCOP.x - RightROI.x;
    var totalx = dxl + dxr;
    var xl = measurementsRef.current.right.pressure / 100 * totalx;
    var xr = measurementsRef.current.left.pressure / 100 * totalx;
    if (xl < dxl) { xr = dxl / xl * xr; xl = dxl; }
    else if (xr < dxr) { xl = dxr / xr * xl; xr = dxr; }

    //Calculating Total Rect
    var upperdistl = LeftCOP.y - LeftROI.y;
    var upperdistr = RightCOP.y - RightROI.y;

    var upperdist = Math.max(upperdistl, upperdistr);

    var lowerdistl = (LeftROI.y + LeftROI.height) - LeftCOP.y;
    var lowerdistr = (RightROI.y + RightROI.height) - RightCOP.y;
    var Marg = 10;
    var lowerdist = Math.max(lowerdistl, lowerdistr);
    var Height = Math.max(lowerdist, upperdist) * 2 + Marg;

    var leftdist = xl + (LeftCOP.x - LeftROI.x);
    var rightdist = xr + (RightROI.x + RightROI.width - RightCOP.x);
    var Width = Math.max(leftdist, rightdist) * 2 + Marg;

    //Calculating new rect positions
    var leftx = Width / 2 - leftdist;
    var lefty = Height / 2 - upperdistl;
    var rightx = Width / 2 + xr - (RightCOP.x - RightROI.x);
    var righty = Height / 2 - upperdistr;

    var frame = pressureData?.CalibFrames_Left[0].clone();
    const maxRenderValue = fp.getMaxValue(frame);

    if (fullCanvasRef.current) {
      const canvas = fullCanvasRef.current;
      const canvasP = fullPdfCanvasRef.current;
      canvas.width = Width * 5;
      canvas.height = Height * 5;
      const ctx = canvas.getContext("2d")!;

      canvasP.width = Width * 5;
      canvasP.height = Height * 5;
      const ctxp = canvasP.getContext("2d")!;


      const colorScale = 255.0 / maxRenderValue;
      const frameByte = new cv.Mat();
      frame.convertTo(frameByte, cv.CV_8U, colorScale);

      const resized = new cv.Mat();
      const dsize = new cv.Size(frameByte.cols * 5, frameByte.rows * 5);
      // cv.INTER_CUBIC is the equivalent of Bicubic interpolation.
      cv.resize(frameByte, resized, dsize, cv.INTER_CUBIC);

      const smoothed = new cv.Mat();
      cv.bilateralFilter(resized, smoothed, 9, 75, 75, cv.BORDER_DEFAULT);

      let colorizedPdf = await ApplyCustomColorMap(smoothed, 'print')
      let colorized = await ApplyCustomColorMap(smoothed);

      let aa = convertMatToImageData(colorized);
      const bitmap = await createImageBitmap(aa);

      let aap = convertMatToImageData(colorizedPdf);
      const bitmapP = await createImageBitmap(aap);

      ctx.drawImage(
        bitmap,
        LeftROI.x * 5, LeftROI.y * 5, LeftROI.width * 5, LeftROI.height * 5,
        leftx * 5, lefty * 5, LeftROI.width * 5, LeftROI.height * 5
      );

      ctx.drawImage(
        bitmap,
        RightROI.x * 5, RightROI.y * 5, RightROI.width * 5, RightROI.height * 5,
        rightx * 5, righty * 5, RightROI.width * 5, RightROI.height * 5
      );

      ctx.strokeStyle = 'rgb(200, 200, 200)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(10, (Height / 2) * 5);
      ctx.lineTo(Width * 5 - 10, (Height / 2) * 5);
      ctx.moveTo((Width / 2) * 5, 20);
      ctx.lineTo((Width / 2) * 5, Height * 5 - 20);
      ctx.stroke();

      ctx.strokeStyle = 'rgb(230, 230, 230)';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(canvas.width / 2, canvas.height / 2, canvas.width / 1.9 - 10, 0, 2 * Math.PI);
      ctx.stroke();

      ctx.font = '20px Arial';
      ctx.fillStyle = 'rgb(230, 230, 230)';

      const measureText = (text: string) => ctx.measureText(text);

      var leftUpStr = measurementsRef.current.left.foreFoot.toFixed(1) + '%';
      var leftUpStrSize = measureText(leftUpStr);
      var leftDownStr = measurementsRef.current.left.rearFoot.toFixed(1) + '%';
      var leftDownStrSize = measureText(leftDownStr);
      var rightUpStr = measurementsRef.current.right.foreFoot.toFixed(1) + '%';
      var rightUpStrSize = measureText(rightUpStr);
      var rightDownStr = measurementsRef.current.right.rearFoot.toFixed(1) + '%';
      var rightDownStrSize = measureText(rightDownStr);

      ctx.fillText(leftUpStr, 0, leftUpStrSize.actualBoundingBoxAscent);
      ctx.fillText(leftDownStr, 0, canvas.height - leftDownStrSize.actualBoundingBoxDescent);
      ctx.fillText(rightUpStr, canvas.width - rightUpStrSize.width, rightUpStrSize.actualBoundingBoxAscent);
      ctx.fillText(rightDownStr, canvas.width - rightDownStrSize.width, canvas.height - rightDownStrSize.actualBoundingBoxDescent);

      ctxp.drawImage(
        bitmapP,
        LeftROI.x * 5, LeftROI.y * 5, LeftROI.width * 5, LeftROI.height * 5,
        leftx * 5, lefty * 5, LeftROI.width * 5, LeftROI.height * 5
      );

      ctxp.drawImage(
        bitmapP,
        RightROI.x * 5, RightROI.y * 5, RightROI.width * 5, RightROI.height * 5,
        rightx * 5, righty * 5, RightROI.width * 5, RightROI.height * 5
      );

      ctxp.strokeStyle = 'rgba(0, 0, 0, 1)';
      ctxp.lineWidth = 1;
      ctxp.beginPath();
      ctxp.moveTo(10, (Height / 2) * 5);
      ctxp.lineTo(Width * 5 - 10, (Height / 2) * 5);
      ctxp.moveTo((Width / 2) * 5, 20);
      ctxp.lineTo((Width / 2) * 5, Height * 5 - 20);
      ctxp.stroke();

      ctxp.strokeStyle = 'rgba(0, 0, 0, 1)';
      ctxp.lineWidth = 3;
      ctxp.beginPath();
      ctxp.arc(canvasP.width / 2, canvasP.height / 2, canvasP.width / 1.9 - 10, 0, 2 * Math.PI);
      ctxp.stroke();

      ctxp.font = '20px Arial';

      const measureTextPdf = (text: string) => ctxp.measureText(text);

      ctxp.fillStyle = '#000';
      ctxp.fillText(leftUpStr, 0, leftUpStrSize.actualBoundingBoxAscent);
      ctxp.fillText(leftDownStr, 0, canvasP.height - leftDownStrSize.actualBoundingBoxDescent);
      ctxp.fillText(rightUpStr, canvasP.width - rightUpStrSize.width, rightUpStrSize.actualBoundingBoxAscent);
      ctxp.fillText(rightDownStr, canvasP.width - rightDownStrSize.width, canvasP.height - rightDownStrSize.actualBoundingBoxDescent);
      const url = canvasP.toDataURL("image/png");
      onFullCanvasReady(url);

      frameByte.delete();
      resized.delete();
      smoothed.delete();
      colorized.delete();
    }
  }

  const CalculateStaticTestFootType = () => {
    if (!pressureData) return;

    const LeftForeSensors = pressureData.Zones_Left.GetForeFootZoneSensors(pressureData.ROI_Left);
    const LeftMidSensors = pressureData.Zones_Left.GetMidFootZoneSensors(pressureData.ROI_Left);
    const LeftRearSensors = pressureData.Zones_Left.GetRearFootZoneSensors(pressureData.ROI_Left);

    const LeftForeSurface = fp.GetSurface(LeftForeSensors, pressureData.CalibMaxFrame_Left);
    const LeftMidSurface = fp.GetSurface(LeftMidSensors, pressureData.CalibMaxFrame_Left);
    const LeftRearSurface = fp.GetSurface(LeftRearSensors, pressureData.CalibMaxFrame_Left);
    const LeftTotalSurface = LeftForeSurface + LeftMidSurface + LeftRearSurface;
    pressureData.FootIndex_Left = Math.round(LeftMidSurface / LeftTotalSurface * 100.0);

    const RightForeSensors = pressureData.Zones_Right.GetForeFootZoneSensors(pressureData.ROI_Right);
    const RightMidSensors = pressureData.Zones_Right.GetMidFootZoneSensors(pressureData.ROI_Right);
    const RightRearSensors = pressureData.Zones_Right.GetRearFootZoneSensors(pressureData.ROI_Right);

    const RightForeSurface = fp.GetSurface(RightForeSensors, pressureData.CalibMaxFrame_Right);
    const RightMidSurface = fp.GetSurface(RightMidSensors, pressureData.CalibMaxFrame_Right);
    const RightRearSurface = fp.GetSurface(RightRearSensors, pressureData.CalibMaxFrame_Right);
    const RightTotalSurface = RightForeSurface + RightMidSurface + RightRearSurface;
    pressureData.FootIndex_Right = Math.round(RightMidSurface / RightTotalSurface * 100.0);
  }

  const FillDynamicAnalyseData = () => {
    if (!pressureData) return;

    const LeftForeSensors = pressureData.Zones_Left.GetForeFootZoneSensors(pressureData.ROI_Left);
    const LeftMidSensors = pressureData.Zones_Left.GetMidFootZoneSensors(pressureData.ROI_Left);
    const LeftRearSensors = pressureData.Zones_Left.GetRearFootZoneSensors(pressureData.ROI_Left);

    const LeftForeSurface = fp.GetSurface(LeftForeSensors, pressureData.CalibMaxFrame_Left);
    const LeftMidSurface = fp.GetSurface(LeftMidSensors, pressureData.CalibMaxFrame_Left);
    const LeftRearSurface = fp.GetSurface(LeftRearSensors, pressureData.CalibMaxFrame_Left);
    const LeftTotalSurface = LeftForeSurface + LeftMidSurface + LeftRearSurface;
    const LeftForeImpulse = fp.GetSumPressure(LeftForeSensors, pressureData.CalibMaxFrame_Left);
    const LeftMidImpulse = fp.GetSumPressure(LeftMidSensors, pressureData.CalibMaxFrame_Left);
    const LeftRearImpulse = fp.GetSumPressure(LeftRearSensors, pressureData.CalibMaxFrame_Left);
    const LeftTotalImpulse = LeftForeImpulse + LeftMidImpulse + LeftRearImpulse;
    var LeftAI = pressureData.FootIndex_Left = Math.round(LeftMidSurface / LeftTotalSurface * 100.0);

    //         XYDiagram diagram = chartControl1.Diagram as XYDiagram;
    // diagram.SecondaryAxesX[0].CustomLabels.Clear();

    // var leftSeries = chartControl1.Series["Left Series"];
    // leftSeries.Points.Clear();

    // if (LeftAI < 20) {
    //   leftSeries.Points.AddPoint("Left Foot", Math.Round(LeftAI, 2));
    //   diagram.SecondaryAxesX[0].CustomLabels.Add(new CustomAxisLabel(Math.Round(LeftAI, 2).ToString(), "Left Foot"));
    // }
    // else if (LeftAI >= 20 && LeftAI < 26) {
    //   var d1 = 20 + ((LeftAI - 20) / 6 * 20);
    //   leftSeries.Points.AddPoint("Left Foot", Math.Round(d1, 2));
    //   diagram.SecondaryAxesX[0].CustomLabels.Add(new CustomAxisLabel(Math.Round(LeftAI, 2).ToString(), "Left Foot"));
    // }
    // else {
    //   var d2 = 40 + ((LeftAI - 26) / 9 * 20);
    //   if (d2 > 60) d2 = 60;
    //   leftSeries.Points.AddPoint("Left Foot", Math.Round(d2, 2));
    //   diagram.SecondaryAxesX[0].CustomLabels.Add(new CustomAxisLabel(Math.Round(LeftAI, 2).ToString(), "Left Foot"));
    // }

    // LeftViewer.SetFootLabel("Left Foot");

    const RightForeSensors = pressureData.Zones_Right.GetForeFootZoneSensors(pressureData.ROI_Right);
    const RightMidSensors = pressureData.Zones_Right.GetMidFootZoneSensors(pressureData.ROI_Right);
    const RightRearSensors = pressureData.Zones_Right.GetRearFootZoneSensors(pressureData.ROI_Right);

    const RightForeSurface = fp.GetSurface(RightForeSensors, pressureData.CalibMaxFrame_Right);
    const RightMidSurface = fp.GetSurface(RightMidSensors, pressureData.CalibMaxFrame_Right);
    const RightRearSurface = fp.GetSurface(RightRearSensors, pressureData.CalibMaxFrame_Right);
    const RightTotalSurface = RightForeSurface + RightMidSurface + RightRearSurface;
    const RightForeImpulse = fp.GetSumPressure(RightForeSensors, pressureData.CalibMaxFrame_Right);
    const RightMidImpulse = fp.GetSumPressure(RightMidSensors, pressureData.CalibMaxFrame_Right);
    const RightRearImpulse = fp.GetSumPressure(RightRearSensors, pressureData.CalibMaxFrame_Right);
    const RightTotalImpulse = RightForeImpulse + RightMidImpulse + RightRearImpulse;
    const RightAI = pressureData.FootIndex_Right = Math.round(RightMidSurface / RightTotalSurface * 100.0);

    // var rightSeries = chartControl1.Series["Right Series"];
    // rightSeries.Points.Clear();

    // if (RightAI < 20) {
    //   rightSeries.Points.Add(new SeriesPoint("Right Foot", Math.Round(RightAI, 2)));
    //   diagram.SecondaryAxesX[0].CustomLabels.Add(new CustomAxisLabel(Math.Round(RightAI, 2).ToString(), "Right Foot"));
    // }
    // else if (RightAI >= 20 && RightAI < 26) {
    //   var d1 = 20 + ((RightAI - 20) / 6 * 20);
    //   rightSeries.Points.Add(new SeriesPoint("Right Foot", Math.Round(d1, 2)));
    //   diagram.SecondaryAxesX[0].CustomLabels.Add(new CustomAxisLabel(Math.Round(RightAI, 2).ToString(), "Right Foot"));
    // }
    // else {
    //   var d2 = 40 + ((RightAI - 26) / 9 * 20);
    //   if (d2 > 60) d2 = 60;
    //   rightSeries.Points.Add(new SeriesPoint("Right Foot", Math.Round(d2, 2)));
    //   diagram.SecondaryAxesX[0].CustomLabels.Add(new CustomAxisLabel(Math.Round(RightAI, 2).ToString(), "Right Foot"));
    // }

    // RightViewer.SetFootLabel("Right Foot");

    setMeasurements(prev => {
      const updated = {
        ...prev,
        left: {
          ...prev.left,
          foreFoot: Math.round(LeftForeSurface / LeftTotalSurface * 100 * 10) / 10,
          midFoot: Math.round(LeftMidSurface / LeftTotalSurface * 100 * 10) / 10,
          rearFoot: Math.round(LeftRearSurface / LeftTotalSurface * 100 * 10) / 10,
          foreFootImpulse: Math.round(LeftForeImpulse / LeftTotalImpulse * 100 * 10) / 10,
          midFootImpulse: Math.round(LeftMidImpulse / LeftTotalImpulse * 100 * 10) / 10,
          rearFootImpulse: Math.round(LeftRearImpulse / LeftTotalImpulse * 100 * 10) / 10,
          footType: GetFootType(LeftAI).toString(),
          axis: Math.round(fp.GetExteriorAngleDegree(pressureData.Axis_Left, new Line(new cv.Point(0, 0), new cv.Point(0, 1))) * 10) / 10,
          index: pressureData.FootIndex_Left
        },
        right: {
          ...prev.right,
          foreFoot: Math.round(RightForeSurface / RightTotalSurface * 100 * 10) / 10,
          midFoot: Math.round(RightMidSurface / RightTotalSurface * 100 * 10) / 10,
          rearFoot: Math.round(RightRearSurface / RightTotalSurface * 100 * 10) / 10,
          foreFootImpulse: Math.round(RightForeImpulse / RightTotalImpulse * 100 * 10) / 10,
          midFootImpulse: Math.round(RightMidImpulse / RightTotalImpulse * 100 * 10) / 10,
          rearFootImpulse: Math.round(RightRearImpulse / RightTotalImpulse * 100 * 10) / 10,
          footType: GetFootType(RightAI).toString(),
          axis: Math.round(-fp.GetExteriorAngleDegree(pressureData.Axis_Right, new Line(new cv.Point(0, 0), new cv.Point(0, 1))) * 10) / 10,
          index: pressureData.FootIndex_Right
        }
      };
      measurementsRef.current = updated;
      return updated;
    });
  }

  const GetFootType = (AI: number) => {
    if (AI <= 20) return InsoleType.PesCavus;
    else if (AI < 26) return InsoleType.Standard;
    else if (AI < 28) return InsoleType.PesPlanusMild;
    else if (AI < 30) return InsoleType.PesPlanusModerate;
    else if (AI < 34) return InsoleType.PesPlanusSevere;
    else return InsoleType.PesPlanusVerySevere;
  }

  const DoAutomaticCalculations = (AutoCrop: boolean, AutoDimension: boolean, reCalculateZones: boolean) => {
    if (!pressureData) return;

    //the condition should be updated !!!!!
    // if (firstTimeZoneCalculation.current) {
    //   firstTimeZoneCalculation.current = false;
    //   SetDefaultControlPoints(AutoCrop, AutoDimension);
    // }

    if (pressureData.ExamType == 'dynamic') {
      if (reCalculateZones) {
        if (AutoCrop) {
          pressureData.ROI_Left = fp.autoCropROI(pressureData.CalibMaxFrame_Left);
          pressureData.ROI_Right = fp.autoCropROI(pressureData.CalibMaxFrame_Right);
        }

        // if (!findZoneMorePrecisionCheckEdit.Checked) {
        //   ExtractDynamicFeetControlPoints();
        //   reCalculateAffineParameter = 30;
        // }

        // else {
        //   List < PointF > currentLeftControlPoints = mPressureData.LeftControlPoints;
        //   List < PointF > currentRightControlPoints = mPressureData.RightControlPoints;

        //   ExtractDynamicFeetControlPoints(reCalculateAffineParameter);
        //   if (!registeredCorrectly("left")) mPressureData.LeftControlPoints = currentLeftControlPoints;
        //   if (!registeredCorrectly("right")) mPressureData.RightControlPoints = currentRightControlPoints;
        //   reCalculateAffineParameter += 10;

        //   leftFootRegistrationFailure = false;
        //   rightFootRegistrationFailure = false;
        // }

        if (AutoDimension) {
          const FootPoint_Right = fp.GetAllFootPoints(pressureData.CalibMaxFrame_Right, pressureData.ROI_Right);
          const actual_Right = fp.convexHull(FootPoint_Right);
          const LongestLines_Right = fp.Get2LongestLineSegments(fp.GetLineSegmentOfPolygon(actual_Right));

          const FootPoint_Left = fp.GetAllFootPoints(pressureData.CalibMaxFrame_Left, pressureData.ROI_Left);
          const actual_Left = fp.convexHull(FootPoint_Left);
          const LongestLines_Left = fp.Get2LongestLineSegments(fp.GetLineSegmentOfPolygon(actual_Left));

          // if (leftFootRegistrationFailure) {
          //   List < Point > HeelPoints_Left = fp.GetHeelPoints(FootPoint_Left, mPressureData.ROI_Left);

          //                   PointF Left_HeelCenter = new PointF();
          //                   float Left_Radius = new float();
          //   fp.FindMinimalBoundingCircle(HeelPoints_Left, out Left_HeelCenter, out Left_Radius);

          //   mPressureData.Axis_Left = fp.GetHullAxisLine(mPressureData.CalibMaxFrame_Left, mPressureData.ROI_Left, mPressureData.COPs_Left, LongestLines_Left, Left_HeelCenter, "Left");
          //   mPressureData.WidthLine_Left = fp.GetWidthLine(mPressureData.Axis_Left, mPressureData.ROI_Left, mPressureData.CalibMaxFrame_Left);
          //   mPressureData.HeelLine_Left = fp.GetHeelLine(mPressureData.Axis_Left, mPressureData.ROI_Left, mPressureData.CalibMaxFrame_Left);
          // }
          // else {
          pressureData.Axis_Left = new Line(pressureData.LeftControlPoints[1], pressureData.LeftControlPoints[19]);
          pressureData.WidthLine_Left = new Line(pressureData.LeftControlPoints[3], pressureData.LeftControlPoints[8]);
          pressureData.HeelLine_Left = new Line(pressureData.LeftControlPoints[18], pressureData.LeftControlPoints[20]);
          // }

          // if (rightFootRegistrationFailure) {
          //   List < Point > HeelPoints_Right = fp.GetHeelPoints(FootPoint_Right, mPressureData.ROI_Right);

          //                   PointF Right_HeelCenter = new PointF();
          //                   float Right_Radius = new float();
          //   fp.FindMinimalBoundingCircle(HeelPoints_Right, out Right_HeelCenter, out Right_Radius);

          //   mPressureData.Axis_Right = fp.GetHullAxisLine(mPressureData.CalibMaxFrame_Right, mPressureData.ROI_Right, mPressureData.COPs_Right, LongestLines_Right, Right_HeelCenter, "Right");
          //   mPressureData.WidthLine_Right = fp.GetWidthLine(mPressureData.Axis_Right, mPressureData.ROI_Right, mPressureData.CalibMaxFrame_Right);
          //   mPressureData.HeelLine_Right = fp.GetHeelLine(mPressureData.Axis_Right, mPressureData.ROI_Right, mPressureData.CalibMaxFrame_Right);
          // }
          // else {
          pressureData.Axis_Right = new Line(pressureData.RightControlPoints[1], pressureData.RightControlPoints[19]);
          pressureData.WidthLine_Right = new Line(pressureData.RightControlPoints[3], pressureData.RightControlPoints[8]);
          pressureData.HeelLine_Right = new Line(pressureData.RightControlPoints[18], pressureData.RightControlPoints[20]);
          // }

          pressureData.MetLine_Left = fp.GetMetatarsalLine(pressureData.Axis_Left, LongestLines_Left, "Left");
          pressureData.MetLine_Right = fp.GetMetatarsalLine(pressureData.Axis_Right, LongestLines_Right, "Right");
        }

        // if (leftFootRegistrationFailure) {
        //   mPressureData.Zones_Left = fp.GetZones(mPressureData.MetLine_Left, mPressureData.WidthLine_Left, mPressureData.HeelLine_Left, mPressureData.ROI_Left, "Left");
        // }
        // else {
        pressureData.Zones_Left = fp.GetZonesByControlPoints(pressureData.LeftControlPoints, pressureData.WidthLine_Left, pressureData.HeelLine_Left, pressureData.ROI_Left, "Left");
        setLeftZones(pressureData.Zones_Left);
        // }

        // if (rightFootRegistrationFailure) {
        //   mPressureData.Zones_Right = fp.GetZones(mPressureData.MetLine_Right, mPressureData.WidthLine_Right, mPressureData.HeelLine_Right, mPressureData.ROI_Right, "Right");
        // }
        // else {
        pressureData.Zones_Right = fp.GetZonesByControlPoints(pressureData.RightControlPoints, pressureData.WidthLine_Right, pressureData.HeelLine_Right, pressureData.ROI_Right, "Right");
        setRightZones(pressureData.Zones_Right);
        // }
      }
      // else if (multiStep) {
      //   if (AutoCrop) {
      //     mPressureData.ROI_Left = lastDynamicAveragePressureData.ROI_Left;
      //     mPressureData.ROI_Right = lastDynamicAveragePressureData.ROI_Right;
      //   }

      //   mPressureData.LeftControlPoints = lastDynamicAveragePressureData.LeftControlPoints;
      //   mPressureData.RightControlPoints = lastDynamicAveragePressureData.RightControlPoints;

      //   if (AutoDimension) {
      //     mPressureData.LongestLines_Left = lastDynamicAveragePressureData.LongestLines_Left;
      //     mPressureData.LongestLines_Right = lastDynamicAveragePressureData.LongestLines_Right;

      //     mPressureData.Axis_Left = lastDynamicAveragePressureData.Axis_Left;
      //     mPressureData.WidthLine_Left = lastDynamicAveragePressureData.WidthLine_Left;
      //     mPressureData.HeelLine_Left = lastDynamicAveragePressureData.HeelLine_Left;

      //     mPressureData.Axis_Right = lastDynamicAveragePressureData.Axis_Right;
      //     mPressureData.WidthLine_Right = lastDynamicAveragePressureData.WidthLine_Right;
      //     mPressureData.HeelLine_Right = lastDynamicAveragePressureData.HeelLine_Right;

      //     mPressureData.MetLine_Left = lastDynamicAveragePressureData.MetLine_Left;
      //     mPressureData.MetLine_Right = lastDynamicAveragePressureData.MetLine_Right;
      //   }

      //   mPressureData.Zones_Left = lastDynamicAveragePressureData.Zones_Left;
      //   mPressureData.Zones_Right = lastDynamicAveragePressureData.Zones_Right;
      // }

      //COP Calculations
      pressureData.COPs_Left = fp.GetCOPArray(pressureData.CalibFrames_Left, pressureData.ROI_Left);
      pressureData.COPs_Right = fp.GetCOPArray(pressureData.CalibFrames_Right, pressureData.ROI_Right);
    }
    else if (pressureData.ExamType == 'static') {
      if (AutoCrop) {
        pressureData.ROI_Left = fp.autoCropROILeft(pressureData.CalibMaxFrame_Left);
        pressureData.ROI_Right = fp.autoCropROIRight(pressureData.CalibMaxFrame_Right);
      }

      // if (!findZoneMorePrecisionCheckEdit.Checked) {
      //   ExtractStaticFeetControlPoints();
      //   reCalculateAffineParameter = 30;
      // }
      // else {
      // List < PointF > currentLeftControlPoints = mPressureData.LeftControlPoints;
      // List < PointF > currentRightControlPoints = mPressureData.RightControlPoints;

      // ExtractStaticFeetControlPoints(reCalculateAffineParameter);
      // if (!registeredCorrectly("left")) mPressureData.LeftControlPoints = currentLeftControlPoints;
      // if (!registeredCorrectly("right")) mPressureData.RightControlPoints = currentRightControlPoints;
      // reCalculateAffineParameter += 10;

      // leftFootRegistrationFailure = false;
      // rightFootRegistrationFailure = false;
      // }

      //COP Calculations
      pressureData.COPs_Left = fp.GetCOPArray(pressureData.CalibFrames_Left, pressureData.ROI_Left);
      pressureData.COPs_Right = fp.GetCOPArray(pressureData.CalibFrames_Right, pressureData.ROI_Right);

      if (AutoDimension) {
        // if (leftFootRegistrationFailure) {
        //                 Rectangle Left_ROI = mPressureData.ROI_Left;
        //                 Rectangle Left_TOPROI = new Rectangle(Left_ROI.Left, Left_ROI.Top, Left_ROI.Width, (int)(Left_ROI.Height / 3.0f));
        //                 Rectangle Left_BottomROI = new Rectangle(Left_ROI.Left, Left_ROI.Top + (int)(Left_ROI.Height * 0.66f), Left_ROI.Width, (int)(Left_ROI.Height / 3.0f));

        //   List < PointF > HelperCOPs_Left = new List<PointF>();
        //   HelperCOPs_Left.Add(fp.getCOPArray(mPressureData.CalibFrames_Left, Left_BottomROI)[0]);
        //   HelperCOPs_Left.Add(fp.getCOPArray(mPressureData.CalibFrames_Left, Left_TOPROI)[0]);

        //   mPressureData.Axis_Left = fp.GetAxisLine(HelperCOPs_Left, mPressureData.ROI_Left, mPressureData.CalibMaxFrame_Left);
        //   mPressureData.WidthLine_Left = fp.GetWidthLine(mPressureData.Axis_Left, mPressureData.ROI_Left, mPressureData.CalibMaxFrame_Left);
        //   mPressureData.HeelLine_Left = fp.GetHeelLine(mPressureData.Axis_Left, mPressureData.ROI_Left, mPressureData.CalibMaxFrame_Left);
        // }
        // else {
        pressureData.Axis_Left = new Line(pressureData.LeftControlPoints[1], pressureData.LeftControlPoints[19]);
        pressureData.WidthLine_Left = new Line(pressureData.LeftControlPoints[3], pressureData.LeftControlPoints[8]);
        pressureData.HeelLine_Left = new Line(pressureData.LeftControlPoints[18], pressureData.LeftControlPoints[20]);
        // }

        // if (rightFootRegistrationFailure) {
        //                 Rectangle Right_ROI = pressureData.ROI_Right;
        //                 Rectangle Right_TOPROI = new Rectangle(Right_ROI.Left, Right_ROI.Top, Right_ROI.Width, (int)(Right_ROI.Height / 3.0f));
        //                 Rectangle Right_BottomROI = new Rectangle(Right_ROI.Left, Right_ROI.Top + (int)(Right_ROI.Height * 0.66f), Right_ROI.Width, (int)(Right_ROI.Height / 3.0f));

        //   List < PointF > HelperCOPs_Right = new List<PointF>();
        //   HelperCOPs_Right.Add(fp.getCOPArray(pressureData.CalibFrames_Right, Right_BottomROI)[0]);
        //   HelperCOPs_Right.Add(fp.getCOPArray(pressureData.CalibFrames_Right, Right_TOPROI)[0]);

        //   pressureData.Axis_Right = fp.GetAxisLine(HelperCOPs_Right, pressureData.ROI_Right, pressureData.CalibMaxFrame_Right);
        //   pressureData.WidthLine_Right = fp.GetWidthLine(pressureData.Axis_Right, pressureData.ROI_Right, pressureData.CalibMaxFrame_Right);
        //   pressureData.HeelLine_Right = fp.GetHeelLine(pressureData.Axis_Right, pressureData.ROI_Right, pressureData.CalibMaxFrame_Right);
        // }
        // else {
        pressureData.Axis_Right = new Line(pressureData.RightControlPoints[1], pressureData.RightControlPoints[19]);
        pressureData.WidthLine_Right = new Line(pressureData.RightControlPoints[3], pressureData.RightControlPoints[8]);
        pressureData.HeelLine_Right = new Line(pressureData.RightControlPoints[18], pressureData.RightControlPoints[20]);
        // }
      }

      // if (leftFootRegistrationFailure) {
      pressureData.Zones_Left = fp.GetZones(pressureData.Axis_Left, pressureData.WidthLine_Left, pressureData.HeelLine_Left, pressureData.ROI_Left, "Left");
      setLeftZones(pressureData.Zones_Left);
      // }
      // else {
      //   mPressureData.Zones_Left = fp.GetZones(
      //     mPressureData.LeftControlPoints,
      //     mPressureData.Axis_Left, mPressureData.WidthLine_Left, mPressureData.HeelLine_Left, mPressureData.ROI_Left, "Left");
      // }

      // if (rightFootRegistrationFailure) {
      pressureData.Zones_Right = fp.GetZones(pressureData.Axis_Right, pressureData.WidthLine_Right, pressureData.HeelLine_Right, pressureData.ROI_Right, "Right");
      setRightZones(pressureData.Zones_Right);
      // }
      // else {
      //   mPressureData.Zones_Right = fp.GetZones(
      //     mPressureData.RightControlPoints,
      //     mPressureData.Axis_Right, mPressureData.WidthLine_Right, mPressureData.HeelLine_Right, mPressureData.ROI_Right, "Right");
      // }
    }
  }

  const SetDefaultControlPoints = (AutoCrop: boolean, AutoDimension: boolean) => {
    if (!pressureData) return;

    if (pressureData.ExamType == 'dynamic') {
      if (AutoCrop) {
        pressureData.ROI_Left = fp.autoCropROI(pressureData.CalibMaxFrame_Left);
        pressureData.ROI_Right = fp.autoCropROI(pressureData.CalibMaxFrame_Right);
      }

      //COP Calculations
      pressureData.COPs_Left = fp.GetCOPArray(pressureData.CalibFrames_Left, pressureData.ROI_Left);
      pressureData.COPs_Right = fp.GetCOPArray(pressureData.CalibFrames_Right, pressureData.ROI_Right);

      if (AutoDimension) {

        let FootPoint_Right = fp.GetAllFootPoints(pressureData.CalibMaxFrame_Right, pressureData.ROI_Right);
        const actual_Right = fp.convexHull(FootPoint_Right);
        const LongestLines_Right = fp.Get2LongestLineSegments(fp.GetLineSegmentOfPolygon(actual_Right));

        let FootPoint_Left = fp.GetAllFootPoints(pressureData.CalibMaxFrame_Left, pressureData.ROI_Left);
        const actual_Left = fp.convexHull(FootPoint_Left);
        const LongestLines_Left = fp.Get2LongestLineSegments(fp.GetLineSegmentOfPolygon(actual_Left));

        // pressureData.LongestLines_Left = LongestLines_Left;
        // pressureData.LongestLines_Right = LongestLines_Right;

        const FootPointArray_Left = fp.GetAllFootPoints(pressureData.CalibMaxFrame_Left, pressureData.ROI_Left);
        const HeelPoints_Left = fp.GetHeelPoints(FootPointArray_Left, pressureData.ROI_Left);
        const Left_HeelCenter = fp.FindMinimalBoundingCircle(HeelPoints_Left);

        pressureData.Axis_Left = fp.GetHullAxisLine(pressureData.CalibMaxFrame_Left, pressureData.ROI_Left, pressureData.COPs_Left, LongestLines_Left, Left_HeelCenter, "Left");
        pressureData.WidthLine_Left = fp.getWidthLine(pressureData.Axis_Left, pressureData.ROI_Left, pressureData.CalibMaxFrame_Left);
        pressureData.HeelLine_Left = fp.getHeelLine(pressureData.Axis_Left, pressureData.ROI_Left, pressureData.CalibMaxFrame_Left);

        const FootPointArray_Right = fp.GetAllFootPoints(pressureData.CalibMaxFrame_Right, pressureData.ROI_Right);
        const HeelPoints_Right = fp.GetHeelPoints(FootPointArray_Right, pressureData.ROI_Right);
        var Right_HeelCenter = fp.FindMinimalBoundingCircle(HeelPoints_Right);

        pressureData.Axis_Right = fp.GetHullAxisLine(pressureData.CalibMaxFrame_Right, pressureData.ROI_Right, pressureData.COPs_Right, LongestLines_Right, Right_HeelCenter, "Right");
        pressureData.WidthLine_Right = fp.getWidthLine(pressureData.Axis_Right, pressureData.ROI_Right, pressureData.CalibMaxFrame_Right);
        pressureData.HeelLine_Right = fp.getHeelLine(pressureData.Axis_Right, pressureData.ROI_Right, pressureData.CalibMaxFrame_Right);


        pressureData.MetLine_Left = fp.GetMetatarsalLine(pressureData.Axis_Left, LongestLines_Left, "Left");
        pressureData.MetLine_Right = fp.GetMetatarsalLine(pressureData.Axis_Right, LongestLines_Right, "Right");
      }
      pressureData.Zones_Left = fp.GetZones(pressureData.MetLine_Left, pressureData.WidthLine_Left, pressureData.HeelLine_Left, pressureData.ROI_Left, "Left");
      pressureData.Zones_Right = fp.GetZones(pressureData.MetLine_Right, pressureData.WidthLine_Right, pressureData.HeelLine_Right, pressureData.ROI_Right, "Right");
      // pressureData.Zones_Left = fp.GetZonesByControlPoints(pressureData.LeftControlPoints, pressureData.WidthLine_Left, pressureData.HeelLine_Left, pressureData.ROI_Left, "Left");
      // pressureData.Zones_Right = fp.GetZonesByControlPoints(pressureData.RightControlPoints, pressureData.WidthLine_Right, pressureData.HeelLine_Right, pressureData.ROI_Right, "Right");
    }
    else if (pressureData.ExamType == 'static') {
      // if (AutoCrop) {
      //   pressureData.ROI_Left = fp.autoCropROILeft(pressureData.CalibMaxFrame_Left);
      //   pressureData.ROI_Right = fp.autoCropROIRight(pressureData.CalibMaxFrame_Right);
      // }

      pressureData.COPs_Left = fp.GetCOPArray(pressureData.CalibFrames_Left, pressureData.ROI_Left);
      pressureData.COPs_Right = fp.GetCOPArray(pressureData.CalibFrames_Right, pressureData.ROI_Right);

      if (AutoDimension) {
        const leftROI = pressureData.ROI_Left;
        const left_TOPROI = new cv.Rect(leftROI.x, leftROI.y, leftROI.width, leftROI.height / 3.0);
        const left_BottomROI = new cv.Rect(leftROI.x, leftROI.y + (leftROI.height * 0.66), leftROI.width, leftROI.height / 3.0);

        var HelperCOPs_Left: cv.Point[] = [];
        HelperCOPs_Left.push(fp.GetCOPArray(pressureData.CalibFrames_Left, left_BottomROI)[0]);
        HelperCOPs_Left.push(fp.GetCOPArray(pressureData.CalibFrames_Left, left_TOPROI)[0]);

        pressureData.Axis_Left = fp.getAxisLine(HelperCOPs_Left, pressureData.ROI_Left, pressureData.CalibMaxFrame_Left);
        pressureData.WidthLine_Left = fp.getWidthLine(pressureData.Axis_Left, pressureData.ROI_Left, pressureData.CalibMaxFrame_Left);
        pressureData.HeelLine_Left = fp.GetHeelLine(pressureData.Axis_Left, pressureData.ROI_Left, pressureData.CalibMaxFrame_Left);

        const rightROI = pressureData.ROI_Right;
        const right_TOPROI = new cv.Rect(rightROI.x, rightROI.y, rightROI.width, rightROI.height / 3.0);
        const right_BottomROI = new cv.Rect(rightROI.x, rightROI.y + (rightROI.height * 0.66), rightROI.width, rightROI.height / 3.0);

        var HelperCOPs_Right: cv.Point[] = [];
        HelperCOPs_Right.push(fp.GetCOPArray(pressureData.CalibFrames_Right, right_BottomROI)[0]);
        HelperCOPs_Right.push(fp.GetCOPArray(pressureData.CalibFrames_Right, right_TOPROI)[0]);

        pressureData.Axis_Right = fp.getAxisLine(HelperCOPs_Right, pressureData.ROI_Right, pressureData.CalibMaxFrame_Right);
        pressureData.WidthLine_Right = fp.getWidthLine(pressureData.Axis_Right, pressureData.ROI_Right, pressureData.CalibMaxFrame_Right);
        pressureData.HeelLine_Right = fp.GetHeelLine(pressureData.Axis_Right, pressureData.ROI_Right, pressureData.CalibMaxFrame_Right);
      }
      pressureData.Zones_Left = fp.GetZones(pressureData.Axis_Left, pressureData.WidthLine_Left, pressureData.HeelLine_Left, pressureData.ROI_Left, "Left");
      pressureData.Zones_Right = fp.GetZones(pressureData.Axis_Right, pressureData.WidthLine_Right, pressureData.HeelLine_Right, pressureData.ROI_Right, "Right");

      // pressureData.Zones_Left = fp.GetZonesByControlPoints(pressureData.LeftControlPoints, pressureData.WidthLine_Left, pressureData.HeelLine_Left, pressureData.ROI_Left, "Left");
      // pressureData.Zones_Right = fp.GetZonesByControlPoints(pressureData.RightControlPoints, pressureData.WidthLine_Right, pressureData.HeelLine_Right, pressureData.ROI_Right, "Right");
    }
    const length_Right = Math.round(fp.getRealDistance(pressureData.Axis_Right.start, pressureData?.Axis_Right.end));
    const length_Left = Math.round(fp.getRealDistance(pressureData.Axis_Left.start, pressureData?.Axis_Left.end));
    pressureData.FootLength_Left = pressureData.FootLength_Right = (length_Left + length_Right) / 2;

    // defaultLeftControlPoints = pressureData.Zones_Left.ControlPoints;
    // defaultRightControlPoints = pressureData.Zones_Right.ControlPoints;
  }

  const readPalleteImage = async (callback: (mat: cv.Mat) => void) => {
    let img = new Image();
    img.src = './Settings/pallete.png';
    img.crossOrigin = "Anonymous";

    img.onload = function () {
      let canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      let ctx = canvas.getContext("2d");
      ctx!.drawImage(img, 0, 0);

      let srcMat = cv.imread(canvas);
      callback(srcMat);
    };
  };

  const readPrintPalleteImage = async (callback: (mat: cv.Mat) => void) => {
    let img = new Image();
    img.src = './Settings/palletePrint.png';
    img.crossOrigin = "Anonymous";

    img.onload = function () {
      let canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      let ctx = canvas.getContext("2d");
      ctx!.drawImage(img, 0, 0);

      let srcMat = cv.imread(canvas);
      callback(srcMat);
    };
  };

  function GenerateCustomColormap(mode?: string) {
    return new Promise((resolve) => {
      if (mode == 'print') {
        readPrintPalleteImage((im) => {
          const customLUT = [];
          for (let i = 0; i < 256; i++) {
            let color = im.ucharPtr(0, 255 - i);
            let b = color[2];
            let g = color[1];
            let r = color[0];
            customLUT.push([b, g, r]); // OpenCV uses BGR order
          }
          im.delete();
          resolve(customLUT);
        });
      } else {
        readPalleteImage((im) => {
          const customLUT = [];
          for (let i = 0; i < 256; i++) {
            let color = im.ucharPtr(0, 255 - i);
            let b = color[2];
            let g = color[1];
            let r = color[0];
            customLUT.push([b, g, r]); // OpenCV uses BGR order
          }
          im.delete();
          resolve(customLUT);
        });
      }
    });
  }

  async function ApplyCustomColorMap(srcMat: cv.Mat, mode?: string) {
    const colorMapCustom = await GenerateCustomColormap(mode); // wait here

    let dst = new cv.Mat();
    cv.cvtColor(srcMat, dst, cv.COLOR_GRAY2BGR);

    for (let r = 0; r < srcMat.rows; r++) {
      for (let c = 0; c < srcMat.cols; c++) {
        let pixelValue = srcMat.ucharAt(r, c); // 0-255
        const color = colorMapCustom[pixelValue]; // Now it's available
        dst.ucharPtr(r, c)[0] = color[0]; // Blue
        dst.ucharPtr(r, c)[1] = color[1]; // Green
        dst.ucharPtr(r, c)[2] = color[2]; // Red
      }
    }

    return dst;
  }

  function convertMatToImageData(imgMat: any) {
    // Convert grayscale to RGBA
    let rgbaMat = new cv.Mat();
    // cvtColor(imgMat, rgbaMat, COLOR_GRAY2RGBA);
    cv.cvtColor(imgMat, rgbaMat, cv.COLOR_BGR2RGBA);
    let imgData = new ImageData(new Uint8ClampedArray(rgbaMat.data), rgbaMat.cols, rgbaMat.rows);
    return imgData;
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    if (activeEdit !== null && newValue === 0) {
      return;
    }
    if (activeEdit === null && newValue === 1) {
      setPreviousTab(activeTab);
    }
    setActiveTab(newValue);
  };

  const handleEditClick = (type: 'dimensions' | 'zones') => {
    setPrevShowDimensions(showDimensions);
    setPrevShowZones(showZones);
    setPreviousTab(activeTab);
    if (type === 'dimensions') {
      setShowDimensions(true);
      setShowZones(false);
    } else {
      setShowDimensions(false);
      setShowZones(true);
    }
    setActiveEdit(type);
    setActiveTab(1);
  };

  const handleCancelEdit = () => {
    discardDrafts();
    setShowDimensions(prevShowDimensions);
    setShowZones(prevShowZones);
    setActiveEdit(null);
    setActiveTab(previousTab);
    setIsDirty(false);
  };

  const handleResetClicked = () => {
    if (!pressureData) return;
    pressureData.status = "scanned";
    UpdateView(true);
  }

  const commitDrafts = () => {
    if (!pressureData) return;

    // detect what changed
    const leftDimsChanged = Boolean(leftDraft?.dimensions);
    const leftZonesChanged = Boolean(leftDraft?.zones);
    const rightDimsChanged = Boolean(rightDraft?.dimensions);
    const rightZonesChanged = Boolean(rightDraft?.zones);

    // LEFT
    if (leftDimsChanged) {
      pressureData.Axis_Left = new Line(leftDraft!.dimensions!.axis.start, leftDraft!.dimensions!.axis.end);
      pressureData.WidthLine_Left = new Line(leftDraft!.dimensions!.width.start, leftDraft!.dimensions!.width.end);
      pressureData.HeelLine_Left = new Line(leftDraft!.dimensions!.heel.start, leftDraft!.dimensions!.heel.end);
      const leftFootLength = Math.round(fp.getRealDistance(pressureData.Axis_Left.start, pressureData?.Axis_Left.end));
      pressureData.FootLength_Left = pressureData.FootLength_Right = (leftFootLength + pressureData.FootLength_Right) / 2;

      leftPressureViewer.current?.setPressureData(
        pressureData.ROI_Left,
        pressureData.COPs_Left,
        pressureData.Axis_Left,
        pressureData.WidthLine_Left,
        pressureData.HeelLine_Left,
      );
    }
    if (leftZonesChanged) {
      pressureData.Zones_Left.controlPoints = leftDraft!.zones!.controlPoints.slice();

      leftPressureViewer.current?.setZones(pressureData.Zones_Left);
      setLeftZones(pressureData.Zones_Left);
    }

    // RIGHT
    if (rightDimsChanged) {
      pressureData.Axis_Right = new Line(rightDraft!.dimensions!.axis.start, rightDraft!.dimensions!.axis.end);
      pressureData.WidthLine_Right = new Line(rightDraft!.dimensions!.width.start, rightDraft!.dimensions!.width.end);
      pressureData.HeelLine_Right = new Line(rightDraft!.dimensions!.heel.start, rightDraft!.dimensions!.heel.end);
      const rightFootLength = Math.round(fp.getRealDistance(pressureData.Axis_Right.start, pressureData?.Axis_Right.end));
      pressureData.FootLength_Right = pressureData.FootLength_Left = (pressureData.FootLength_Left + rightFootLength) / 2;

      rightPressureViewer.current?.setPressureData(
        pressureData.ROI_Right,
        pressureData.COPs_Right,
        pressureData.Axis_Right,
        pressureData.WidthLine_Right,
        pressureData.HeelLine_Right
      );
    }
    if (rightZonesChanged) {
      pressureData.Zones_Right.controlPoints = rightDraft!.zones!.controlPoints.slice();

      rightPressureViewer.current?.setZones(pressureData.Zones_Right);
      setRightZones(pressureData.Zones_Right);
    }

    // persist
    pressureData.status = "scanned";

    UpdateView(false);

    // cleanup UI state
    setLeftDraft(null);
    setRightDraft(null);
    setIsDirty(false);
    setActiveEdit(null);
    setShowDimensions(prevShowDimensions);
    setShowZones(prevShowZones);
    setActiveTab(previousTab);
  };

  const discardDrafts = () => {
    if (!pressureData) return;

    leftPressureViewer.current?.setPressureData(
      pressureData.ROI_Left,
      pressureData.COPs_Left,
      pressureData.Axis_Left,
      pressureData.WidthLine_Left,
      pressureData.HeelLine_Left
    );
    leftPressureViewer.current?.setZones(pressureData.Zones_Left);

    rightPressureViewer.current?.setPressureData(
      pressureData.ROI_Right,
      pressureData.COPs_Right,
      pressureData.Axis_Right,
      pressureData.WidthLine_Right,
      pressureData.HeelLine_Right
    );
    rightPressureViewer.current?.setZones(pressureData.Zones_Right);

    setLeftDraft(null);
    setRightDraft(null);
    setIsDirty(false);
  };

  async function handleExportPDF() {
    if (!printRef.current) return;

    // optional small delay to let subcomponents settle
    await new Promise(r => setTimeout(r, 250));

    const canvas = await html2canvas(printRef.current, {
      scale: 2, useCORS: true, backgroundColor: null
    });

    const pdf = new jsPDF({ unit: "pt", format: "a4" }); // 595x842pt
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const imgW = pageW;
    const imgH = (canvas.height * imgW) / canvas.width;

    // multi-page slice if taller than one page
    let y = 0;
    const sliceHpx = Math.floor((pageH * canvas.width) / pageW);
    const pageCanvas = document.createElement("canvas");
    const ctx = pageCanvas.getContext("2d")!;

    while (y < canvas.height) {
      pageCanvas.width = canvas.width;
      pageCanvas.height = Math.min(sliceHpx, canvas.height - y);
      ctx.clearRect(0, 0, pageCanvas.width, pageCanvas.height);
      ctx.drawImage(canvas, 0, y, pageCanvas.width, pageCanvas.height, 0, 0, pageCanvas.width, pageCanvas.height);
      const dataUrl = pageCanvas.toDataURL("image/png", 0.98);
      if (y > 0) pdf.addPage();
      const h = (pageCanvas.height * imgW) / pageCanvas.width;
      pdf.addImage(dataUrl, "PNG", 0, 0, imgW, h, undefined, "FAST");
      y += sliceHpx;
    }

    // pdf.save(`pressure-report-${isDynamic ? "dynamic" : "static"}.pdf`);
    const url = pdf.output("bloburl");
    window.open(url, "_blank", "noopener,noreferrer");
  }

  const SetStaticView = () => {
    setShowDimensions(false);
    setShowZones(false);
    setShowMeasurements(false);
    setShowCursors(false);

    //  rightPressureViewer.current.ShowCharts(false);
    //  leftPressureViewer.current.ShowCharts(false);
  }

  const SetDynamicView = () => {
    setShowDimensions(false);
    setShowZones(false);
    setShowMeasurements(false);
    setShowCursors(false);

    // rightPressureViewer.current.ShowCharts(true);
    // leftPressureViewer.current.ShowCharts(true);
  }

  const InitializeGalleryItemsCheckValue = async () => {
    const pressureDataList = pressureData?.pressureDataList ?? [];
    for (let index = 0; index < pressureDataList.length; index++) {
      const pd = pressureDataList[index];
      if (pd.CalibFrames_Left.length > 0) {
        galleryHandler.current?.setCalibMaxFrame(pd.CalibMaxFrame_Left);
        galleryHandler.current?.setPressureData(pd.ROI_Left, pd.COPs_Left, pd.Axis_Left, pd.WidthLine_Left, pd.HeelLine_Left);

        const isSelected = pressureData?.leftSelectedTestsIndexes.includes(index);
        await galleryHandler.current?.getPrintBitmap(88, 104, isSelected ? 'Dynamic_Auto' : 'Black_White').then(cnv => {
          const newItem = matToDataURL(cnv);
          setLeftGallery(prv => prv.includes(newItem) ? prv : [...prv, newItem]);
        });
      }

      if (pd.CalibFrames_Right.length > 0) {
        galleryHandler.current?.setCalibMaxFrame(pd.CalibMaxFrame_Right);
        galleryHandler.current?.setPressureData(pd.ROI_Right, pd.COPs_Right, pd.Axis_Right, pd.WidthLine_Right, pd.HeelLine_Right);

        const isSelected = pressureData?.rightSelectedTestsIndexes.includes(index);

        await galleryHandler.current?.getPrintBitmap(88, 104, isSelected ? 'Dynamic_Auto' : 'Black_White').then(cnv => {
          const newItem = matToDataURL(cnv);
          setRightGallery(prv => prv.includes(newItem) ? prv : [...prv, newItem]);
        });
      }
    }
  }

  const matToDataURL = (cnvs: any) => {
    // const canvas = document.createElement('canvas');
    // cv.imshow(canvas, mat);
    const dataUrl = cnvs.toDataURL('image/png');
    cnvs.remove();
    return dataUrl;
  };

  const handleImageClick = async (foot: number, index: number) => {
    if (!pressureData?.pressureDataList) return;

    const pd = pressureData?.pressureDataList[index];

    if (foot === 0) {
      galleryHandler.current?.setCalibMaxFrame(pd.CalibMaxFrame_Right);
      galleryHandler.current?.setPressureData(pd.ROI_Right, pd.COPs_Right, pd.Axis_Right, pd.WidthLine_Right, pd.HeelLine_Right);
      let isSelected = false;

      if (!pressureData?.rightSelectedTestsIndexes.includes(index)) {
        pressureData?.rightSelectedTestsIndexes.push(index);
        isSelected = true;
      }
      else {
        if (pressureData?.rightSelectedTestsIndexes.length > 1) {
          const idx = pressureData?.rightSelectedTestsIndexes.indexOf(index);
          if (idx !== -1) {
            pressureData?.rightSelectedTestsIndexes.splice(idx, 1);
          }
        }
        else {
          return;
        }
      }

      await galleryHandler.current?.getPrintBitmap(88, 104, isSelected ? 'Dynamic_Auto' : 'Black_White').then(cnv => {
        const newItem = matToDataURL(cnv);
        setRightGallery(prv => {
          const updated = [...prv];
          updated[index] = newItem;
          return updated;
        });

        pressureData.getAverageDynamicPressureData();
        rightPressureViewer.current?.setCalibFrameArray(pressureData.CalibFrames_Right);
        rightPressureViewer.current?.setCalibMaxFrame(pressureData.CalibMaxFrame_Right);
        UpdateView(false);
        rightPressureViewer.current?.setPressureData(pressureData.ROI_Right, pressureData.COPs_Right, pressureData.Axis_Right, pressureData.WidthLine_Right, pressureData.HeelLine_Right);
      });
    }
    else {
      galleryHandler.current?.setCalibMaxFrame(pd.CalibMaxFrame_Left);
      galleryHandler.current?.setPressureData(pd.ROI_Left, pd.COPs_Left, pd.Axis_Left, pd.WidthLine_Left, pd.HeelLine_Left);
      let isSelected = false;

      if (!pressureData?.leftSelectedTestsIndexes.includes(index)) {
        pressureData?.leftSelectedTestsIndexes.push(index);
        isSelected = true;
      }
      else {
        if (pressureData?.leftSelectedTestsIndexes.length > 1) {
          const idx = pressureData?.leftSelectedTestsIndexes.indexOf(index);
          if (idx !== -1) {
            pressureData?.leftSelectedTestsIndexes.splice(idx, 1);
          }
        }
        else {
          return;
        }
      }
      await galleryHandler.current?.getPrintBitmap(88, 104, isSelected ? 'Dynamic_Auto' : 'Black_White').then(cnv => {
        const newItem = matToDataURL(cnv);
        setLeftGallery(prv => {
          const updated = [...prv];
          updated[index] = newItem;
          return updated;
        });
        pressureData.getAverageDynamicPressureData();
        leftPressureViewer.current?.setCalibFrameArray(pressureData.CalibFrames_Left);
        leftPressureViewer.current?.setCalibMaxFrame(pressureData.CalibMaxFrame_Left);
        UpdateView(false);
        leftPressureViewer.current?.setPressureData(pressureData.ROI_Left, pressureData.COPs_Left, pressureData.Axis_Left, pressureData.WidthLine_Left, pressureData.HeelLine_Left);
      });
    }
  };

  const getAverageDynamicPressureData = () => {
    if (!pressureData?.pressureDataList) return;

    if (pressureData.leftSelectedTestsIndexes.length > 1) {
      const numOfLeftFrames = pressureData.pressureDataList[0].CalibFrames_Left.length;
      const numOfLeftFootTests = pressureData.leftSelectedTestsIndexes.length;
      const leftFrameWidth = pressureData.pressureDataList[0].CalibFrames_Left[0].cols;
      const leftFrameHeight = pressureData.pressureDataList[0].CalibFrames_Left[0].rows;

      const averageFramesLeft = [];
      const averageMaxLeft = new cv.Mat(leftFrameHeight, leftFrameWidth, cv.CV_16UC1, new cv.Scalar(0));

      for (let i = 0; i < numOfLeftFrames; i++) {
        const nullFrame = new cv.Mat(leftFrameHeight, leftFrameWidth, cv.CV_16UC1, new cv.Scalar(0));
        averageFramesLeft.push(nullFrame);
      }

      for (let i = 0; i < numOfLeftFootTests; i++) {
        const pd = pressureData.pressureDataList[pressureData.leftSelectedTestsIndexes[i]];

        for (let j = 0; j < pd.CalibFrames_Left.length; j++) {
          cv.add(averageFramesLeft[j], pd.CalibFrames_Left[j], averageFramesLeft[j]);
        }
        cv.add(averageMaxLeft, pd.CalibMaxFrame_Left, averageMaxLeft);
      }

      const divisorLeft = new cv.Mat(leftFrameHeight, leftFrameWidth, cv.CV_16UC1, new cv.Scalar(numOfLeftFootTests));

      for (let i = 0; i < averageFramesLeft.length; i++) {
        cv.divide(averageFramesLeft[i], divisorLeft, averageFramesLeft[i]);
      }
      cv.divide(averageMaxLeft, divisorLeft, averageMaxLeft);

      pressureData.CalibFrames_Left = averageFramesLeft.slice();
      pressureData.CalibMaxFrame_Left = averageMaxLeft.clone();
    }
    else {
      pressureData.CalibFrames_Left = pressureData.pressureDataList[pressureData.leftSelectedTestsIndexes[0]].CalibFrames_Left.slice();
      pressureData.CalibMaxFrame_Left = pressureData.pressureDataList[pressureData.leftSelectedTestsIndexes[0]].CalibMaxFrame_Left.clone();
    }

    if (pressureData.rightSelectedTestsIndexes.length > 1) {
      const numOfRightFrames = pressureData.pressureDataList[0].CalibFrames_Right.length;
      const numOfRightFootTests = pressureData.rightSelectedTestsIndexes.length;
      const rightFrameWidth = pressureData.pressureDataList[0].CalibFrames_Right[0].cols;
      const rightFrameHeight = pressureData.pressureDataList[0].CalibFrames_Right[0].rows;

      const averageFramesRight = [];
      const averageMaxRight = new cv.Mat(rightFrameHeight, rightFrameWidth, cv.CV_16UC1, new cv.Scalar(0));

      for (let i = 0; i < numOfRightFrames; i++) {
        const nullFrame = new cv.Mat(rightFrameHeight, rightFrameWidth, cv.CV_16UC1, new cv.Scalar(0));
        averageFramesRight.push(nullFrame);
      }

      for (let i = 0; i < numOfRightFootTests; i++) {
        const pd = pressureData.pressureDataList[pressureData.rightSelectedTestsIndexes[i]];

        if (pd.CalibFrames_Right.length > 0 && pressureData.rightSelectedTestsIndexes.includes(i)) {
          for (let j = 0; j < pd.CalibFrames_Right.length; j++) {
            cv.add(averageFramesRight[j], pd.CalibFrames_Right[j], averageFramesRight[j]);
          }
          cv.add(averageMaxRight, pd.CalibMaxFrame_Right, averageMaxRight);
        }
      }

      const divisorRight = new cv.Mat(rightFrameHeight, rightFrameWidth, cv.CV_16UC1, new cv.Scalar(numOfRightFootTests));

      for (let i = 0; i < averageFramesRight.length; i++) {
        cv.divide(averageFramesRight[i], divisorRight, averageFramesRight[i]);
      }
      cv.divide(averageMaxRight, divisorRight, averageMaxRight);

      pressureData.CalibFrames_Right = averageFramesRight.slice();
      pressureData.CalibMaxFrame_Right = averageMaxRight.clone();
    }
    else {
      pressureData.CalibFrames_Right = pressureData.pressureDataList[pressureData.rightSelectedTestsIndexes[0]].CalibFrames_Right.slice();
      pressureData.CalibMaxFrame_Right = pressureData.pressureDataList[pressureData.rightSelectedTestsIndexes[0]].CalibMaxFrame_Right.clone();
    }
    pressureData.status = 'scanned';
  }

  useEffect(() => {
    if (pressureData == null || !leftPressureViewer.current || !rightPressureViewer.current)
      return;

    let firstTimeLoading = false;

    if (pressureData.pressureDataList && pressureData.pressureDataList.length > 1) {
      if (pressureData.leftSelectedTestsIndexes.length === 0 || pressureData.rightSelectedTestsIndexes.length === 0) {
        firstTimeLoading = true;
      }

      setMultiStep(true);
      InitializeGalleryItemsCheckValue();
      // getAverageDynamicPressureData();
    }

    leftPressureViewer.current.setFrameTimes(pressureData.FrameTimes_Left);
    leftPressureViewer.current.setCalibFrameArray(pressureData.CalibFrames_Left);
    leftPressureViewer.current.setCalibMaxFrame(pressureData.CalibMaxFrame_Left);

    rightPressureViewer.current.setFrameTimes(pressureData.FrameTimes_Right);
    rightPressureViewer.current.setCalibFrameArray(pressureData.CalibFrames_Right);
    rightPressureViewer.current.setCalibMaxFrame(pressureData.CalibMaxFrame_Right);

    fp.sensorHeight = pressureData.PressureSensorHeight;
    fp.sensorWidth = pressureData.PressureSensorWidth;

    leftPressureViewer.current.setProcessData(pressureData.PressureSensorWidth, pressureData.PressureSensorHeight, fp);
    rightPressureViewer.current.setProcessData(pressureData.PressureSensorWidth, pressureData.PressureSensorHeight, fp);

    if (pressureData.ExamType === 'static') {
      leftPressureViewer.current.setStaticView();
      rightPressureViewer.current.setStaticView();

      // SetSingleTestPanelsView();
    }
    else {
      leftPressureViewer.current.setDynamicView();
      rightPressureViewer.current.setDynamicView();
    }

    UpdateView(true, firstTimeLoading);

    if (pressureData.ExamType == 'static') {
      SetStaticView();
    }
    else {
      SetDynamicView();

      //if (!multipleDynamicTest) {
      //    SetSingleDynamicTestPanelsView();
      //}

      //else {
      //    SetMultipleDynamicTestPanelsView(230);
      //}
    }

  }, [])

  return (
    <Box sx={{
      // display: activeTab === 1 ? 'flex' : 'none',
      display: 'flex',
      flexDirection: isStackedLayout ? 'column' : 'row',
      gap: 0.5,
      backgroundColor: isDarkMode ? '#2D2D2D' : 'rgba(250, 250, 250, 0.95)',
      // boxShadow: '0 15px 40px rgba(0, 0, 0, 0.30)',
      backdropFilter: 'blur(0px)',
      WebkitBackdropFilter: 'blur(0px)',
      border: '1px solid',
      borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0,0,0,0.12)',
      /*
       * `overflow: hidden` rather than `visible`: this box has a backdrop-filter,
       * which makes it the containing block for its fixed-position children, so
       * the 794x1120 off-screen PrintViewer counts as its scrollable overflow.
       * Letting that escape added ~1120px of dead scroll under the panel. Height
       * stays `auto`, so no in-flow content is clipped -- and the desktop branch
       * has always contained it the same way via `overflow: auto`.
       */
      ...(isNarrowLayout
        ? { height: 'auto', minHeight: 0, maxHeight: 'none', overflow: 'hidden' }
        : { height: '700px', minHeight: '700px', maxHeight: '700px', overflow: 'auto' }),
      width: '100%',
      minWidth: 0,
      boxSizing: 'border-box',
      flexGrow: 1,
      p: 0.5
    }}>

      {/* Tables */}
      <Box sx={{
        width: isStackedLayout ? '100%' : 'fit-content',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: isNarrowLayout ? 0.5 : 1,
        border: '1px solid #ccc',
        borderRadius: 1,
        padding: isNarrowLayout ? 0.75 : 1,
        backgroundColor: isDarkMode ? '#333' : '#fff',
        height: isNarrowLayout ? 'auto' : '100%',
        boxSizing: 'border-box',
      }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            mb: isNarrowLayout ? 0.5 : 2,
            minHeight: isNarrowLayout ? '36px' : '40px',
            '& .MuiTabs-flexContainer': {
              justifyContent: isStackedLayout ? 'center' : undefined,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#076596',
              height: '3px',
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: '700',
              fontSize: '0.9rem',
              color: isDarkMode ? '#fff' : '#076596',
              padding: '6px 12px',
              minWidth: '80px',
              minHeight: '40px',
              '&.Mui-selected': {
                color: '#076596',
                fontWeight: '800',
              },
            },
          }}
        >
          <Tab label={isDynamic ? "Dynamic" : "Static"} disabled={activeEdit !== null} />
          <Tab label="Edit" disabled={activeEdit !== null} />
        </Tabs>

        {activeTab === 0 && (
          isDynamic ? (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: isNarrowLayout ? 0.75 : 1.5,
              width: '100%',
              minWidth: 0,
              ...(isNarrowLayout ? { height: 'auto' } : { height: '100%', overflow: 'auto' }),
            }}>
              {/* Contact Percentages Section */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="h6" sx={{
                  fontWeight: '700',
                  color: isDarkMode ? '#fff' : '#076596',
                  mb: 1,
                  fontSize: '0.85rem',
                  textAlign: 'center'
                }}>
                  Contact Percentages
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1 }}>
                  <Box sx={{
                    border: '1px solid #ccc',
                    backgroundColor: isDarkMode ? '#555' : '#f0f0f0',
                    color: isDarkMode ? '#fff' : '#000',
                    borderRadius: 1,
                    p: 1,
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '0.8rem'
                  }}>
                    Left Foot
                  </Box>
                  <Box sx={{
                    border: '1px solid #ccc',
                    backgroundColor: isDarkMode ? '#555' : '#f0f0f0',
                    color: isDarkMode ? '#fff' : '#000',
                    borderRadius: 1,
                    p: 1,
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '0.8rem'
                  }}>
                    Right Foot
                  </Box>
                </Box>

                {/* Headers */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="body2" sx={{
                    textAlign: 'center',
                    fontWeight: '600',
                    color: isDarkMode ? '#fff' : '#000',
                    fontSize: '0.75rem'
                  }}>
                    Surface
                  </Typography>
                  <Typography variant="body2" sx={{
                    textAlign: 'center',
                    fontWeight: '600',
                    color: isDarkMode ? '#fff' : '#000',
                    fontSize: '0.75rem'
                  }}>
                    Impulse
                  </Typography>
                  <Box></Box>
                  <Typography variant="body2" sx={{
                    textAlign: 'center',
                    fontWeight: '600',
                    color: isDarkMode ? '#fff' : '#000',
                    fontSize: '0.75rem'
                  }}>
                    Surface
                  </Typography>
                  <Typography variant="body2" sx={{
                    textAlign: 'center',
                    fontWeight: '600',
                    color: isDarkMode ? '#fff' : '#000',
                    fontSize: '0.75rem'
                  }}>
                    Impulse
                  </Typography>
                </Box>

                {/* Rows */}
                {[
                  { section: 'rearFoot', impulse: 'rearFootImpulse', label: 'RearFoot' },
                  { section: 'midFoot', impulse: 'midFootImpulse', label: 'MidFoot' },
                  { section: 'foreFoot', impulse: 'foreFootImpulse', label: 'ForeFoot' }
                ].map((item) => (
                  <Box key={item.section} sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0.5, mb: 0.5 }}>
                    <Box sx={{
                      border: '1px solid #ccc',
                      backgroundColor: isDarkMode ? '#444' : '#fff',
                      color: isDarkMode ? '#fff' : '#000',
                      borderRadius: 0.5,
                      p: cellPad,
                      textAlign: 'center',
                      fontSize: cellFontSize,
                      fontWeight: '500'
                    }}>
                      {measurements.left[item.section as keyof typeof measurements.left]}%
                    </Box>
                    <Box sx={{
                      border: '1px solid #ccc',
                      backgroundColor: isDarkMode ? '#444' : '#fff',
                      color: isDarkMode ? '#fff' : '#000',
                      borderRadius: 0.5,
                      p: cellPad,
                      textAlign: 'center',
                      fontSize: cellFontSize,
                      fontWeight: '500'
                    }}>
                      {measurements.left[item.impulse as keyof typeof measurements.left]}%
                    </Box>
                    <Box sx={{
                      border: '1px solid #ccc',
                      backgroundColor: isDarkMode ? '#555' : '#f0f0f0',
                      color: isDarkMode ? '#fff' : '#000',
                      borderRadius: 0.5,
                      p: cellPad,
                      textAlign: 'center',
                      fontWeight: '700',
                      fontSize: isNarrowLayout ? '0.68rem' : '0.75rem'
                    }}>
                      {item.label}
                    </Box>
                    <Box sx={{
                      border: '1px solid #ccc',
                      backgroundColor: isDarkMode ? '#444' : '#fff',
                      color: isDarkMode ? '#fff' : '#000',
                      borderRadius: 0.5,
                      p: cellPad,
                      textAlign: 'center',
                      fontSize: cellFontSize,
                      fontWeight: '500'
                    }}>
                      {measurements.right[item.section as keyof typeof measurements.right]}%
                    </Box>
                    <Box sx={{
                      border: '1px solid #ccc',
                      backgroundColor: isDarkMode ? '#444' : '#fff',
                      color: isDarkMode ? '#fff' : '#000',
                      borderRadius: 0.5,
                      p: cellPad,
                      textAlign: 'center',
                      fontSize: cellFontSize,
                      fontWeight: '500'
                    }}>
                      {measurements.right[item.impulse as keyof typeof measurements.right]}%
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Additional Sections */}
              {[
                { title: 'Foot Type', left: measurements.left.footType, right: measurements.right.footType },
                { title: 'Arch Index', left: measurements.left.index, right: measurements.right.index },
                { title: 'Foot Axis', left: measurements.left.axis, right: measurements.right.axis }
              ].map((section, index) => (
                <Box key={index} sx={isNarrowLayout
                  ? { display: 'grid', gridTemplateColumns: '1fr 1.3fr 1fr', gap: 0.5, alignItems: 'center' }
                  : { display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="h6" sx={{
                    fontWeight: '700',
                    color: isDarkMode ? '#fff' : '#076596',
                    mb: isNarrowLayout ? 0 : 0.5,
                    order: isNarrowLayout ? 2 : undefined,
                    fontSize: isNarrowLayout ? '0.68rem' : '0.8rem',
                    textAlign: 'center'
                  }}>
                    {section.title}
                  </Typography>
                  <Box sx={isNarrowLayout
                    ? { display: 'contents' }
                    : { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
                    <Box sx={{
                      border: '1px solid #ccc',
                      backgroundColor: isDarkMode ? '#444' : '#fff',
                      color: isDarkMode ? '#fff' : '#000',
                      borderRadius: 0.5,
                      p: cellPad,
                      textAlign: 'center',
                      fontSize: cellFontSize,
                      fontWeight: '500',
                      order: isNarrowLayout ? 1 : undefined,
                    }}>
                      {section.left}
                    </Box>
                    <Box sx={{
                      border: '1px solid #ccc',
                      backgroundColor: isDarkMode ? '#444' : '#fff',
                      color: isDarkMode ? '#fff' : '#000',
                      borderRadius: 0.5,
                      p: cellPad,
                      textAlign: 'center',
                      fontSize: cellFontSize,
                      fontWeight: '500',
                      order: isNarrowLayout ? 3 : undefined,
                    }}>
                      {section.right}
                    </Box>
                  </Box>
                </Box>
              ))}

              {/* Checkboxes */}
              <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', gap: 2, mt: isNarrowLayout ? 0 : 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={showDimensions}
                      onChange={(e) => setShowDimensions(e.target.checked)}
                      sx={{ color: isDarkMode ? '#fff' : '#076596' }}
                    />
                  }
                  label={<Typography sx={{
                    color: isDarkMode ? '#fff' : '#076596',
                    fontSize: '0.8rem',
                    fontWeight: '500'
                  }}>
                    Dimensions
                  </Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={showZones}
                      onChange={(e) => setShowZones(e.target.checked)}
                      sx={{ color: isDarkMode ? '#fff' : '#076596' }}
                    />
                  }
                  label={<Typography sx={{
                    color: isDarkMode ? '#fff' : '#076596',
                    fontSize: '0.8rem',
                    fontWeight: '500'
                  }}>
                    Zones
                  </Typography>}
                />
              </Box>

              {/* Print Report Button */}
              <Button
                variant="contained"
                size="small"
                onClick={handleExportPDF}
                sx={{
                  backgroundColor: '#076596',
                  color: '#fff',
                  fontWeight: '600',
                  fontSize: '0.8rem',
                  py: 0.75,
                  '&:hover': { backgroundColor: '#0a6fb4' },
                  mt: 1
                }}
              >
                Print Report
              </Button>
            </Box>
          ) : (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: isNarrowLayout ? 0.75 : 1.5,
              width: '100%',
              minWidth: 0,
              alignItems: 'center',
              ...(isNarrowLayout ? { height: 'auto' } : { height: '100%', overflow: 'auto' }),
            }}>
              {/* Static mode content */}
              {/* Canvas */}
              <Box sx={{
                backgroundColor: '#282a2e',
                border: `1px solid ${isDarkMode ? '#666' : '#076596'}`,
                borderRadius: 1,
                width: isStackedLayout ? '100%' : '70%',
                height: isNarrowLayout ? 180 : 'auto',
                flexShrink: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                <canvas ref={fullCanvasRef} style={{ width: "80%", height: '80%' }} />
              </Box>

              {/* Measurement Grid */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, width: '100%' }}>
                {[
                  { label: 'Pressure %', left: measurements.left.pressure, right: measurements.right.pressure },
                  { label: 'ForeFoot %', left: measurements.left.foreFoot, right: measurements.right.foreFoot },
                  { label: 'RearFoot %', left: measurements.left.rearFoot, right: measurements.right.rearFoot },
                  { label: 'Surface (cm²)', left: measurements.left.surface, right: measurements.right.surface },
                  { label: 'Total Avg (kg/cm²)', left: measurements.left.totalAvg, right: measurements.right.totalAvg },
                  { label: 'ForeFoot Avg', left: measurements.left.foreFootAvg, right: measurements.right.foreFootAvg },
                  { label: 'RearFoot Avg', left: measurements.left.rearFootAvg, right: measurements.right.rearFootAvg },
                ].map((item, index) => (
                  <Box key={index} sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 0.5, alignItems: 'center' }}>
                    <Box sx={{
                      border: '1px solid #ccc',
                      backgroundColor: isDarkMode ? '#444' : '#fff',
                      color: isDarkMode ? '#fff' : '#000',
                      borderRadius: 0.5,
                      p: isNarrowLayout ? 0.35 : 0.5,
                      textAlign: 'center',
                      fontSize: cellFontSize,
                      fontWeight: '500'
                    }}>
                      {item.left}
                    </Box>
                    <Box sx={{
                      border: '1px solid #ccc',
                      backgroundColor: isDarkMode ? '#555' : '#f0f0f0',
                      color: isDarkMode ? '#fff' : '#000',
                      borderRadius: 0.5,
                      p: isNarrowLayout ? 0.35 : 0.5,
                      textAlign: 'center',
                      fontSize: isNarrowLayout ? '0.68rem' : '0.75rem',
                      fontWeight: '600'
                    }}>
                      {item.label}
                    </Box>
                    <Box sx={{
                      border: '1px solid #ccc',
                      backgroundColor: isDarkMode ? '#444' : '#fff',
                      color: isDarkMode ? '#fff' : '#000',
                      borderRadius: 0.5,
                      p: isNarrowLayout ? 0.35 : 0.5,
                      textAlign: 'center',
                      fontSize: cellFontSize,
                      fontWeight: '500'
                    }}>
                      {item.right}
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Checkboxes and Print Button */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={showDimensions}
                        onChange={(e) => setShowDimensions(e.target.checked)}
                        sx={{ color: isDarkMode ? '#fff' : '#076596' }}
                      />
                    }
                    label={<Typography sx={{
                      color: isDarkMode ? '#fff' : '#076596',
                      fontSize: '0.8rem',
                      fontWeight: '500'
                    }}>
                      Dimensions
                    </Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={showZones}
                        onChange={(e) => setShowZones(e.target.checked)}
                        sx={{ color: isDarkMode ? '#fff' : '#076596' }}
                      />
                    }
                    label={<Typography sx={{
                      color: isDarkMode ? '#fff' : '#076596',
                      fontSize: '0.8rem',
                      fontWeight: '500'
                    }}>
                      Zones
                    </Typography>}
                  />
                </Box>

                <Button
                  variant="contained"
                  size="small"
                  onClick={handleExportPDF}
                  sx={{
                    backgroundColor: '#076596',
                    color: '#fff',
                    fontWeight: '600',
                    fontSize: '0.8rem',
                    py: 0.75,
                    '&:hover': { backgroundColor: '#0a6fb4' }
                  }}
                >
                  Print Report
                </Button>
              </Box>
            </Box>
          )
        )}

        {activeTab === 1 && (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            height: isNarrowLayout ? 'auto' : '100%',
            justifyContent: 'flex-start',
            width: isStackedLayout ? '100%' : 'fit-content',
          }}>
            {!activeEdit ? (
              <>
                <Button
                  variant="outlined"
                  startIcon={<ViewModuleOutlined sx={{ fontSize: '1.1rem' }} />}
                  onClick={() => handleEditClick('zones')}
                  disabled={activeEdit !== null}
                  sx={{
                    justifyContent: 'flex-center',
                    gap: 1.5,
                    width: '100%',
                    minWidth: 'fit-content',
                    height: '45px',
                    border: '2px solid #076596',
                    color: isDarkMode ? '#fff' : '#076596',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    textTransform: 'none',
                    '&:hover': {
                      border: '2px solid #0a6fb4',
                      backgroundColor: isDarkMode ? '#444' : '#f0f0f0'
                    }
                  }}
                >
                  Zones
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Height sx={{ fontSize: '1.1rem' }} />}
                  onClick={() => handleEditClick('dimensions')}
                  disabled={activeEdit !== null}
                  sx={{
                    justifyContent: 'flex-center',
                    gap: 1.5,
                    width: '100%',
                    minWidth: 'fit-content',
                    height: '45px',
                    border: '2px solid #076596',
                    color: isDarkMode ? '#fff' : '#076596',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    textTransform: 'none',
                    '&:hover': {
                      border: '2px solid #0a6fb4',
                      backgroundColor: isDarkMode ? '#444' : '#f0f0f0'
                    }
                  }}
                >
                  Foot Dimensions
                </Button>
              </>
            ) : (
              <Box sx={{
                display: 'flex',
                flexDirection: isLandscapeLayout ? 'row' : 'column',
                gap: 1.5,
                width: '100%',
              }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={commitDrafts}
                  disabled={!isDirty}
                  sx={{
                    backgroundColor: '#076596',
                    color: '#fff',
                    fontWeight: '600',
                    fontSize: '0.85rem',
                    py: 1,
                    flex: 1,
                    minWidth: 0,
                    '&:hover': { backgroundColor: '#0a6fb4' },
                    '&:disabled': { backgroundColor: '#ccc' }
                  }}
                >
                  Apply
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleCancelEdit}
                  sx={{
                    backgroundColor: '#666',
                    color: '#fff',
                    fontWeight: '600',
                    fontSize: '0.85rem',
                    py: 1,
                    flex: 1,
                    minWidth: 0,
                    '&:hover': { backgroundColor: '#888' }
                  }}
                >
                  Cancel
                </Button>
              </Box>
            )}

            {!activeEdit && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<Refresh sx={{ fontSize: '1.1rem' }} />}
                onClick={handleResetClicked}
                disabled={activeEdit !== null}
                sx={{
                  justifyContent: 'flex-center',
                  gap: 1.5,
                  width: '100%',
                  height: '45px',
                  border: '2px solid #076596',
                  color: isDarkMode ? '#fff' : '#076596',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  marginTop: 'auto',
                  textTransform: 'none',
                  '&:hover': {
                    border: '2px solid #0a6fb4',
                    backgroundColor: isDarkMode ? '#444' : '#f0f0f0'
                  }
                }}
              >
                Reset
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Pressure Viewers */}
      {isDynamic ? (
        <Box sx={{
          display: 'flex',
          flexDirection: isNarrowLayout
            ? (isStackedLayout ? 'column' : 'row')
            : ((isEditing || (multiStep && activeTab === 1)) ? 'row' : 'column'),
          gap: 0.5,
          minWidth: 0,
          ...(isNarrowLayout
            ? (isStackedLayout
              ? { height: 'auto', minHeight: 0, width: '100%' }
              : { height: 'auto', minHeight: 0, flex: 1, minWidth: 0 })
            : { height: '100%', minHeight: '100%', width: 'fit-content' }),
        }}>
          {(!isEditing && multiStep && activeTab === 1) && (
            <Box sx={isStackedLayout
              ? { display: 'flex', flexDirection: 'column', width: '100%' }
              : { display: 'flex', flexDirection: 'column', flex: 0.15, width: '15%' }}>
              <ImageList
                sx={{ width: '100%', height: isNarrowLayout ? (isStackedLayout ? 120 : 340) : '100%', border: '1px solid #ccc', borderRadius: 2, m: 0 }}
                cols={isStackedLayout ? 3 : 1}
                gap={8}
                rowHeight={104}
              >
                {leftGallery.map((item, index) => (
                  <ImageListItem key={item} sx={{ '&:hover': { backgroundColor: "#b0d3f0" } }}>
                    <img style={{ width: '88px', height: '104px', alignSelf: 'center', padding: '2px' }}
                      src={item}
                      loading="lazy"
                      onClick={() => handleImageClick(1, index)}
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            </Box>
          )}
          <Box
            className={`pressure-viewer-wrapper ${isEditing ? 'pressure-viewer--editing' : ''} size-${activeTab}`}
          >
            <PressureViewerComponent
              ref={leftPressureViewer}
              imgData={null}
              mode="dynamic"
              footLabel="Left"
              stackChart={isNarrowLayout}
              editMode={activeEdit ?? 'none'}
              chartVis={!isEditing && (activeTab !== 1 || !multiStep)}
              onGeometryChange={(payload) => {
                setLeftDraft(prev => {
                  const next = { ...(prev ?? {}) } as any;
                  if (payload.kind === 'dimensions') {
                    next.dimensions = {
                      axis: payload.axis,
                      width: payload.width,
                      heel: payload.heel,
                    };
                  } else {
                    next.zones = { controlPoints: payload.controlPoints };
                  }
                  return next;
                });
                setIsDirty(true);
              }}
            />
          </Box>

          <Box
            className={`pressure-viewer-wrapper ${isEditing ? 'pressure-viewer--editing' : ''} size-${activeTab}`}
          >
            <PressureViewerComponent
              ref={rightPressureViewer}
              imgData={null}
              mode="dynamic"
              footLabel="Right"
              stackChart={isNarrowLayout}
              chartVis={!isEditing && (activeTab !== 1 || !multiStep)}
              editMode={activeEdit ?? 'none'}
              onGeometryChange={(payload) => {
                setRightDraft(prev => {
                  const next = { ...(prev ?? {}) } as any;
                  if (payload.kind === 'dimensions') {
                    next.dimensions = {
                      axis: payload.axis,
                      width: payload.width,
                      heel: payload.heel,
                    };
                  } else {
                    next.zones = { controlPoints: payload.controlPoints };
                  }
                  return next;
                });
                setIsDirty(true);
              }}
            />
          </Box>
          {((activeEdit === 'none' || activeEdit === null) && multiStep && activeTab === 1) && (
            <Box sx={isStackedLayout
              ? { display: 'flex', flexDirection: 'column', width: '100%' }
              : { display: 'flex', flexDirection: 'column', flex: 0.15, width: '15%' }}>
              <ImageList
                sx={{ width: '100%', height: isNarrowLayout ? (isStackedLayout ? 120 : 340) : '100%', border: '1px solid #ccc', borderRadius: 2, m: 0 }}
                cols={isStackedLayout ? 3 : 1}
                gap={8}
                rowHeight={104}
              >
                {rightGallery.map((item, index) => (
                  <ImageListItem key={item} sx={{ '&:hover': { backgroundColor: "#b0d3f0" } }}>
                    <img style={{ width: '88px', height: '104px', alignSelf: 'center', padding: '2px' }}
                      src={item}
                      loading="lazy"
                      onClick={() => handleImageClick(0, index)}
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            </Box>
          )}
        </Box>
      ) : (
        <Box className='pressure-container-static' >
          <Box sx={{
            flex: isStackedLayout ? 'none' : 1,
            border: '1px solid #ccc',
            borderRadius: 1,
            padding: isNarrowLayout ? 1 : 2,
            backgroundColor: isDarkMode ? '#333' : '#fff',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            minWidth: 0,
            alignItems: 'center',
            boxSizing: 'border-box',
            ...(isNarrowLayout
              ? { height: 'auto', maxHeight: 'none', minHeight: 0 }
              : { height: '100%', maxHeight: '100%', minHeight: '100%' }),
          }}>
            <PressureViewerComponent
              ref={leftPressureViewer}
              imgData={null}
              mode="static"
              stackChart={isNarrowLayout}
              footLabel="Left"
              editMode={activeEdit ?? 'none'}
              onGeometryChange={(payload) => {
                setLeftDraft(prev => {
                  const next = { ...(prev ?? {}) } as any;
                  if (payload.kind === 'dimensions') {
                    next.dimensions = {
                      axis: payload.axis,
                      width: payload.width,
                      heel: payload.heel,
                    };
                  } else {
                    next.zones = { controlPoints: payload.controlPoints };
                  }
                  return next;
                });
                setIsDirty(true);
              }}
            />
          </Box>

          <Box sx={{
            flex: isStackedLayout ? 'none' : 1,
            border: '1px solid #ccc',
            borderRadius: 1,
            padding: isNarrowLayout ? 1 : 2,
            backgroundColor: isDarkMode ? '#333' : '#fff',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            minWidth: 0,
            alignItems: 'center',
            boxSizing: 'border-box',
            ...(isNarrowLayout
              ? { height: 'auto', maxHeight: 'none', minHeight: 0 }
              : { height: '100%', maxHeight: '100%', minHeight: '100%' }),
          }}>
            <PressureViewerComponent
              ref={rightPressureViewer}
              imgData={null}
              mode="static"
              stackChart={isNarrowLayout}
              footLabel="Right"
              editMode={activeEdit ?? 'none'}
              onGeometryChange={(payload) => {
                setRightDraft(prev => {
                  const next = { ...(prev ?? {}) } as any;
                  if (payload.kind === 'dimensions') {
                    next.dimensions = {
                      axis: payload.axis,
                      width: payload.width,
                      heel: payload.heel,
                    };
                  } else {
                    next.zones = { controlPoints: payload.controlPoints };
                  }
                  return next;
                });
                setIsDirty(true);
              }}
            />
          </Box>
        </Box>
      )}

      {/* Report */}
      <Box sx={{ position: 'fixed', left: -9999, top: 0 }}>
        {!isDynamic && (
          <Box sx={{
            backgroundColor: '#282a2e',
            border: isDarkMode ? '4px solid rgb(107, 107, 107)' : '4px solid rgba(18,103,147,255)',
            borderRadius: '10px',
            width: '300px',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            overflow: 'auto',
            justifyContent: 'center',
            alignItems: 'center',
            height: '400px'
          }}>
            <canvas ref={fullPdfCanvasRef} style={{ width: "100%", height: '100%' }} />
          </Box>
        )}

        <PrintViewer
          ref={printRef}
          isDynamic={isDynamic}
          measurements={measurements}
          leftImgData={pressureData?.CalibMaxFrame_Left}
          rightImgData={pressureData?.CalibMaxFrame_Right}
          leftRoi={pressureData?.ROI_Left}
          rightRoi={pressureData?.ROI_Right}
          fullCanvasUrl={fullCanvasUrl}
          leftZones={leftZones}
          rightZones={rightZones}
        />
      </Box>

      {/* Gallery Handler */}
      <Box sx={{ display: 'none' }}>
        <PressureViewerComponent ref={galleryHandler} imgData={null} mode={"dynamic"} />
      </Box>
    </Box>

  );
};


export default Viewer;
